import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { formatMoney } from "../../utils/formatMoney";
import { API_BASE_URL, authFetch } from "../../config/api";

const paymentModes = [
  "Petty Cash",
  "Cash",
  "Check",
  "Manager's Check",
  "Telegraphic Transfer",
  "Online Banking Transfer",
];

const MANILA_TIME_ZONE = "Asia/Manila";
const CHECK_CUTOFF_HOUR = 16;
const JOB_ORDER_EDITOR_USER_ID = 155;
const JOB_ORDER_EDITOR_NAME = "Anita Obogne";

const normalizeParticularTaxRate = (particular = {}) => {
  const numericRate = Number(particular?.tax_rate || 0);
  if (numericRate > 0) {
    return numericRate === 2 ? 2 : 12;
  }
  return particular?.with_tax ? 12 : 0;
};

const computeParticularTaxAmount = (grossAmount, taxRate) => {
  const gross = Number(grossAmount || 0);
  const normalizedRate = Number(taxRate || 0);
  if (gross <= 0 || normalizedRate <= 0) return 0;
  return Number(((gross * normalizedRate) / (100 + normalizedRate)).toFixed(2));
};

const EWT_OPTIONS = [
  { value: "", label: "No EWT", rate: 0 },
  { value: "ewt_1", label: "EWT 1%", rate: 1 },
  { value: "ewt_2", label: "EWT 2%", rate: 2 },
  { value: "ewt_5", label: "EWT 5%", rate: 5 },
  { value: "ewt_10", label: "EWT 10%", rate: 10 },
  { value: "specific_amount", label: "Specific Amount", rate: 0 },
];

const TAX_TYPE_OPTIONS = [
  { value: "", label: "No Tax", rate: 0 },
  { value: "tax_12", label: "12%", rate: 12 },
  { value: "specific_amount", label: "Specific Amount", rate: 0 },
];

const computeParticularTaxAmountByType = (lineTotal, taxType, taxAmountInput) => {
  const total = Number(lineTotal || 0);
  if (!taxType || total <= 0) return 0;
  if (taxType === "specific_amount") {
    const override = Number(taxAmountInput || 0);
    return Number(Math.min(Math.max(override, 0), total).toFixed(2));
  }
  const option = TAX_TYPE_OPTIONS.find((o) => o.value === taxType);
  const rate = option?.rate || 0;
  if (rate <= 0) return 0;
  return Number(((total * rate) / (100 + rate)).toFixed(2));
};

const computeParticularEwtAmount = (lineTotal, ewtType, ewtAmountInput) => {
  const total = Number(lineTotal || 0);
  if (!ewtType || total <= 0) return 0;
  if (ewtType === "specific_amount") {
    const override = Number(ewtAmountInput || 0);
    return Number(Math.min(Math.max(override, 0), total).toFixed(2));
  }
  const option = EWT_OPTIONS.find((o) => o.value === ewtType);
  const rate = option?.rate || 0;
  if (rate <= 0) return 0;
  return Number(((total * rate) / 100).toFixed(2));
};

const RequestSummary = () => {
  const createAttachmentRow = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file: null });
  const [data, setData] = useState([]);
  const [checkedByUsers, setCheckedByUsers] = useState([]);
  const [checkedByUserId, setCheckedByUserId] = useState(() => {
    try {
      return sessionStorage.getItem("requestSummaryCheckedByUserId") || "";
    } catch {
      return "";
    }
  });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editAttachments, setEditAttachments] = useState([createAttachmentRow()]);
  const [editError, setEditError] = useState("");
  const [editAmount, setEditAmount] = useState(0);
  const [editQty, setEditQty] = useState(1);
  const [editParticulars, setEditParticulars] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [voucherError, setVoucherError] = useState("");
  const [viewItem, setViewItem] = useState(null);
  const [viewShipment, setViewShipment] = useState(null);
  const [viewTrail, setViewTrail] = useState([]);
  const [viewTrailLoading, setViewTrailLoading] = useState(false);
  const [trailItem, setTrailItem] = useState(null);
  const [deletedRequestsModal, setDeletedRequestsModal] = useState(false);
  const [deletedRequests, setDeletedRequests] = useState([]);
  const [deletedRequestsLoading, setDeletedRequestsLoading] = useState(false);
  const [deletedRequestsSearch, setDeletedRequestsSearch] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [signatureApplying, setSignatureApplying] = useState(false);
  const [signatureFallbackOpen, setSignatureFallbackOpen] = useState(false);
  const [liquidationChoice, setLiquidationChoice] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState("");
  const [resubmitLoadingId, setResubmitLoadingId] = useState(null);
  const [dateFilter, setDateFilter] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customDateDraftStart, setCustomDateDraftStart] = useState("");
  const [customDateDraftEnd, setCustomDateDraftEnd] = useState("");
  const [voucherFilter, setVoucherFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [jobOrderOptions, setJobOrderOptions] = useState([]);
  const [jobOrderSearchInput, setJobOrderSearchInput] = useState("");
  const [jobOrderMenuOpen, setJobOrderMenuOpen] = useState(false);
  const [jobOrderLookupLoading, setJobOrderLookupLoading] = useState(false);
  const [editJobOrderSearch, setEditJobOrderSearch] = useState("");
  const jobOrderFilterRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [joFilter, setJoFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [voucherCounts, setVoucherCounts] = useState({ withVoucher: 0, withoutVoucher: 0 });
  const [cutoffPendingRows, setCutoffPendingRows] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const location = useLocation();
  const isRequestorPortal = location.pathname.startsWith("/requestor");
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const canEditJobOrder = currentUserId === JOB_ORDER_EDITOR_USER_ID;
  const [scopeFilter, setScopeFilter] = useState("created");
  const previousDateFilterRef = useRef("all");

  // Date filter helpers
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filter) {
      case "today":
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case "week": {
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);
        return { start: startOfWeek, end: endOfWeek };
      }
      case "month": {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return { start: startOfMonth, end: endOfMonth };
      }
      case "custom":
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setDate(end.getDate() + 1); // Include end date
          return { start, end };
        }
        return null;
      default:
        return null;
    }
  };

  const formatDateKey = (value) => {
    if (!value) return "—";
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
  };

  const formatDateRangeLabel = (value) => {
    if (!value) return "All Dates";
    const parsed = value instanceof Date ? value : new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime())
      ? String(value)
      : parsed.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getActiveDateRangeLabel = () => {
    if (dateFilter === "all") return "All Dates";
    if (dateFilter === "custom") {
      if (customStartDate && customEndDate) {
        return `${formatDateRangeLabel(customStartDate)} - ${formatDateRangeLabel(customEndDate)}`;
      }
      return "Custom Range";
    }

    const range = getDateRange(dateFilter);
    if (!range) return "All Dates";
    const inclusiveEnd = new Date(range.end.getTime() - 24 * 60 * 60 * 1000);
    return `${formatDateRangeLabel(range.start)} - ${formatDateRangeLabel(inclusiveEnd)}`;
  };

  const getManilaDateKey = (value) => {
    return getManilaParts(value)?.dateKey || "";
  };

  const getManilaParts = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: MANILA_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, part.value])
    );

    return {
      dateKey: `${parts.year}-${parts.month}-${parts.day}`,
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  };

  const isAtOrAfterCutoff = (parts) =>
    Boolean(parts) &&
    (parts.hour > CHECK_CUTOFF_HOUR || (parts.hour === CHECK_CUTOFF_HOUR && parts.minute >= 0));

  const getAmountCurrencyMeta = (currencyValue) => {
    const code = String(currencyValue || "PHP").toUpperCase();
    return {
      code,
      symbol: code === "USD" ? "$" : "₱",
      locale: code === "USD" ? "en-US" : "en-PH",
    };
  };

  const renderCurrencyLine = (currencyValue, amount) => {
    const { code, symbol, locale } = getAmountCurrencyMeta(currencyValue);

    return (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
          {code}
        </span>
        <span>
          {symbol}
          {formatMoney(amount, locale)}
        </span>
      </div>
    );
  };

  const getUsdConvertedAmount = (phpAmount, exchangeRate) => {
    const rate = Number(exchangeRate || 1);
    return rate > 0 ? Number(phpAmount || 0) / rate : Number(phpAmount || 0);
  };

  const renderRequestTotal = (amount, currencyValue, exchangeRate) => {
    const { code } = getAmountCurrencyMeta(currencyValue);
    const phpAmount = Number(amount || 0);

    if (code === "USD" && Number(exchangeRate || 0) > 0) {
      const usdAmount = getUsdConvertedAmount(phpAmount, exchangeRate);

      return (
        <div className="flex flex-col items-end gap-1 leading-tight">
          {renderCurrencyLine("USD", usdAmount)}
          {renderCurrencyLine("PHP", phpAmount)}
        </div>
      );
    }

    return renderCurrencyLine(code, phpAmount);
  };

  const getRequestTotalEwtAmount = (request) => {
    const itemCurrency = String(request?.currency || "PHP").toUpperCase();
    const itemExchangeRate = Number(request?.exchange_rate || 1);
    const isUsdItem = itemCurrency === "USD" && itemExchangeRate > 0;
    return (request?.particulars || []).reduce((sum, p) => {
      const totalOriginal = Number(p.total || Number(p.amount || 0) * Number(p.quantity || 1));
      const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
      const taxRate = normalizeParticularTaxRate(p);
      const taxAmountPhp = taxRate > 0 ? computeParticularTaxAmount(totalPhp, taxRate) : 0;
      const netAmountPhp = totalPhp - taxAmountPhp;
      return sum + computeParticularEwtAmount(netAmountPhp, p.ewt_type, p.ewt_amount);
    }, 0);
  };

  const getRequestTotalAmountPhp = (request) => {
    const currency = String(request?.currency || "PHP").toUpperCase();
    if (currency !== "USD") return Number(request?.total_amount || 0);
    const rate = Number(request?.exchange_rate || 1) || 1;
    const originalTotal = (request?.particulars || []).reduce(
      (sum, particular) => sum + Number(particular.amount || 0) * Number(particular.quantity || 1),
      0
    );
    return (originalTotal || Number(request?.total_amount || 0) / rate) * rate;
  };

  const getRequestNetPayableAmount = (request) => {
    const totalAmountPhp = getRequestTotalAmountPhp(request);
    const totalEwtAmountPhp = getRequestTotalEwtAmount(request);
    return Number((totalAmountPhp - totalEwtAmountPhp).toFixed(2));
  };

  const getRequestTotalHtml = (amount, currencyValue, exchangeRate) => {
    const { code, symbol } = getAmountCurrencyMeta(currencyValue);
    const phpAmount = Number(amount || 0);
    const isUsd = code === "USD" && Number(exchangeRate || 0) > 0;

    const pill = (label) =>
      `<span style="display:inline-flex;align-items:center;border-radius:9999px;background:#e2e8f0;padding:2px 8px;font-size:11px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:#475569;">${label}</span>`;

    if (isUsd) {
      const usdAmount = getUsdConvertedAmount(phpAmount, exchangeRate);
      return `
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;line-height:1.15;">
          <div style="display:inline-flex;align-items:center;gap:8px;">
            ${pill("USD")}
            <span style="font-weight:600;color:#1f2937;">$${formatMoney(usdAmount, "en-US")}</span>
          </div>
          <div style="display:inline-flex;align-items:center;gap:8px;">
            ${pill("PHP")}
            <span style="font-weight:600;color:#1f2937;">₱${formatMoney(phpAmount, "en-PH")}</span>
          </div>
        </div>
      `;
    }

    return `
      <div style="display:inline-flex;align-items:center;gap:8px;">
        ${pill(code)}
        <span style="font-weight:600;color:#1f2937;">${symbol}${formatMoney(phpAmount, code === "USD" ? "en-US" : "en-PH")}</span>
      </div>
    `;
  };

  const todayManilaKey = getManilaDateKey(new Date());

  // Export to Excel — fetches every row matching the active filters (not
  // just the currently loaded page) from a dedicated, unpaginated endpoint.
  const exportToExcel = async () => {
    setExportLoading(true);
    let exportRows = [];
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/requests/export`);
      const params = buildRequestListParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, value);
        }
      });
      const res = await authFetch(url, { credentials: "include" });
      const json = await res.json();
      if (json.success) exportRows = json.data || [];
    } catch (err) {
      console.error("load requests for export", err);
      setExportLoading(false);
      return;
    }

    const uniqueJobOrders = Array.from(
      new Set(
        exportRows
          .map((row) => String(row.job_order || "").trim())
          .filter(Boolean)
      )
    );

    const shipmentByJobOrder = {};

    await Promise.all(
      uniqueJobOrders.map(async (jobOrder) => {
        try {
          const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
          url.searchParams.set("q", jobOrder);
          url.searchParams.set("limit", 1);
          const res = await authFetch(url, { credentials: "include" });
          const json = await res.json();
          if (json.success && Array.isArray(json.data) && json.data.length) {
            shipmentByJobOrder[jobOrder] = json.data[0] || null;
          }
        } catch (err) {
          console.error("load shipment for export", jobOrder, err);
        }
      })
    );

    const exportData = exportRows.map((r) => ({
      "RFP No": r.rfp_no || "",
      "Type": r.type || "",
      "Payee": r.payee || "",
      "Payee Type": r.payee_type || "",
      "Job Order": r.job_order || "",
      "Corporate Client": shipmentByJobOrder[String(r.job_order || "").trim()]?.corporate_client || "",
      "Mode of Payment": r.mode_of_payment || "",
      "Bank": r.bank || "",
      "Check No.": r.check_no || "",
      "For Liquidation": r.for_liquidation ? "Yes" : "No",
      "For Sales": r.type === "non_operational" && r.for_sales ? "Yes" : "No",
      "Preparer": r.preparer_name || "",
      "Status": r.status || "",
      "Remarks": r.remarks || "",
      "Created": r.created_at ? new Date(r.created_at).toLocaleString() : "",
      "Total Amount": Number(r.total_amount || 0),
      "Less: EWT": getRequestTotalEwtAmount(r),
      "Total Amount Due": getRequestNetPayableAmount(r),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    if (exportData.length) {
      XLSX.utils.sheet_add_json(
        ws,
        [
          {
            "RFP No": "Grand Total",
            "Type": "",
            "Payee": "",
            "Payee Type": "",
            "Job Order": "",
            "Corporate Client": "",
            "Mode of Payment": "",
            "Bank": "",
            "Check No.": "",
            "For Liquidation": "",
            "For Sales": "",
            "Preparer": "",
            "Status": "",
            "Remarks": "",
            "Created": "",
            "Total Amount": exportRows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0),
            "Less: EWT": exportRows.reduce((sum, row) => sum + getRequestTotalEwtAmount(row), 0),
            "Total Amount Due": exportRows.reduce((sum, row) => sum + getRequestNetPayableAmount(row), 0),
          },
        ],
        {
          origin: -1,
          skipHeader: true,
        }
      );
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Requests");

    // Generate filename with date filter info
    let filename = "requests_";
    if (dateFilter === "today") {
      filename += `today_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "week") {
      filename += `week_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "month") {
      const now = new Date();
      filename += `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
    } else if (dateFilter === "custom" && customStartDate && customEndDate) {
      filename += `${customStartDate}_to_${customEndDate}`;
    } else {
      filename += "all";
    }
    filename += ".xlsx";

    XLSX.writeFile(wb, filename);
    setExportLoading(false);
  };

  const buildRequestListParams = () => {
    const params = {
      scope: isRequestorPortal ? scopeFilter : "all",
      paymentMode: paymentFilter,
      jobOrder: joFilter,
      type: typeFilter,
      status: statusFilter,
      search: searchTerm,
      voucherFilter,
      dateFilter,
    };
    if (dateFilter === "custom") {
      params.customStart = customStartDate;
      params.customEnd = customEndDate;
    }
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/requests`);
      const params = buildRequestListParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, value);
        }
      });
      url.searchParams.set("page", currentPage);
      url.searchParams.set("pageSize", rowsPerPage);

      const res = await authFetch(url, { credentials: "include" });
      const json = await res.json();
      if (json.success) {
        setData(json.data || []);
        setTotalCount(Number(json.summary?.totalCount || 0));
        setTotalAmount(Number(json.summary?.totalAmount || 0));
      }
    } catch (err) {
      console.error("load requests", err);
    } finally {
      setLoading(false);
    }
  };

  const openCustomDateModal = () => {
    setCustomDateDraftStart(customStartDate);
    setCustomDateDraftEnd(customEndDate);
    setCustomDateModalOpen(true);
  };

  const closeCustomDateModal = () => {
    setCustomDateModalOpen(false);
  };

  const applyCustomDateRange = () => {
    setCustomStartDate(customDateDraftStart);
    setCustomEndDate(customDateDraftEnd);
    setDateFilter("custom");
    setCustomDateModalOpen(false);
  };

  // Debounce free-text search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(searchInput), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const filterKey = JSON.stringify([
    scopeFilter, paymentFilter, joFilter, typeFilter, statusFilter,
    voucherFilter, searchTerm, dateFilter, customStartDate, customEndDate,
  ]);
  const isFirstFilterRun = useRef(true);

  // Any filter change invalidates the current page. Skip on first mount so
  // this doesn't fight with the page/pageSize effect below for the initial load.
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, currentPage, rowsPerPage]);

  const loadFilterScopedWidgets = async () => {
    const scope = isRequestorPortal ? scopeFilter : "all";
    const params = buildRequestListParams();
    const countsUrl = new URL(`${API_BASE_URL}/api/accounting/requests/voucher-counts`);
    Object.entries({
      scope,
      paymentMode: params.paymentMode,
      jobOrder: params.jobOrder,
      type: params.type,
      status: params.status,
      search: params.search,
      dateFilter: params.dateFilter,
      customStart: params.customStart,
      customEnd: params.customEnd,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        countsUrl.searchParams.set(key, value);
      }
    });

    const cutoffUrl = new URL(`${API_BASE_URL}/api/accounting/requests/cutoff-pending`);
    cutoffUrl.searchParams.set("scope", scope);
    const readWidget = async (url, label) => {
      try {
        const response = await authFetch(url, { credentials: "include" });
        return await response.json();
      } catch (err) {
        console.error(label, err);
        return null;
      }
    };

    const [countsJson, cutoffJson] = await Promise.all([
      readWidget(countsUrl, "load voucher counts"),
      readWidget(cutoffUrl, "load cutoff-pending requests"),
    ]);

    if (countsJson?.success) {
      setVoucherCounts({
        withVoucher: Number(countsJson.data?.withVoucher || 0),
        withoutVoucher: Number(countsJson.data?.withoutVoucher || 0),
      });
    }
    if (cutoffJson?.success) setCutoffPendingRows(cutoffJson.data || []);
  };

  useEffect(() => {
    loadFilterScopedWidgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter, paymentFilter, joFilter, typeFilter, statusFilter, searchTerm, dateFilter, customStartDate, customEndDate]);

  // Job Orders are independent of the current request-table filters. Load
  // the complete list only when the access scope changes, then reuse it while
  // the user changes date/status/payment filters.
  useEffect(() => {
    let cancelled = false;
    const scope = isRequestorPortal ? scopeFilter : "all";
    const loadJobOrders = async () => {
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/requests/job-orders`);
        url.searchParams.set("scope", scope);
        const response = await authFetch(url, { credentials: "include" });
        const json = await response.json();
        if (!cancelled && json.success) setJobOrderOptions(json.data || []);
      } catch (error) {
        if (!cancelled) console.error("load job orders", error);
      }
    };

    loadJobOrders();
    return () => {
      cancelled = true;
    };
  }, [isRequestorPortal, scopeFilter]);

  useEffect(() => {
    const handleOutsideJobOrderClick = (event) => {
      if (!jobOrderFilterRef.current?.contains(event.target)) {
        setJobOrderMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideJobOrderClick);
    return () => document.removeEventListener("mousedown", handleOutsideJobOrderClick);
  }, []);

  useEffect(() => {
    const term = jobOrderSearchInput.trim();
    if (!jobOrderMenuOpen || !term) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setJobOrderLookupLoading(true);
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/requests`);
        url.searchParams.set("scope", isRequestorPortal ? scopeFilter : "all");
        url.searchParams.set("search", term);
        url.searchParams.set("dateFilter", "all");
        url.searchParams.set("page", "1");
        url.searchParams.set("pageSize", "100");

        const response = await authFetch(url, { credentials: "include" });
        const json = await response.json();
        if (!cancelled && json.success) {
          const searchedJobOrders = (json.data || [])
            .map((row) => String(row.job_order || "").trim())
            .filter(Boolean);
          setJobOrderOptions((current) =>
            Array.from(new Set([...current, ...searchedJobOrders])).sort((a, b) => a.localeCompare(b))
          );
        }
      } catch (error) {
        if (!cancelled) console.error("lookup job orders", error);
      } finally {
        if (!cancelled) setJobOrderLookupLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [jobOrderMenuOpen, jobOrderSearchInput, isRequestorPortal, scopeFilter]);

  useEffect(() => {
    const loadCheckedByUsers = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/checked-by-users`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) setCheckedByUsers(json.data || []);
      } catch (err) {
        console.error("load checked-by users", err);
      }
    };

    loadCheckedByUsers();
  }, []);

  useEffect(() => {
    try {
      if (checkedByUserId) {
        sessionStorage.setItem("requestSummaryCheckedByUserId", checkedByUserId);
      } else {
        sessionStorage.removeItem("requestSummaryCheckedByUserId");
      }
    } catch {
      // Ignore storage errors.
    }
  }, [checkedByUserId]);

  useEffect(() => {
    // Reset liquidation choice when opening a request view
    if (viewItem) {
      setLiquidationChoice(Boolean(viewItem.for_liquidation));
      setReturnReason("");
      setReturnError("");
      setReturnOpen(false);
      setReturnLoading(false);
    }
  }, [viewItem]);

  // Fetch shipment details for view modal when job_order is available
  useEffect(() => {
    const fetchShipment = async () => {
      if (!viewItem?.job_order) {
        setViewShipment(null);
        return;
      }
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
        url.searchParams.set("q", viewItem.job_order);
        url.searchParams.set("limit", 1);
        const res = await authFetch(url, { credentials: "include" });
        const json = await res.json();
        if (json.success && json.data?.length) setViewShipment(json.data[0]);
        else setViewShipment(null);
      } catch (err) {
        console.error("shipment lookup", err);
        setViewShipment(null);
      }
    };
    fetchShipment();
  }, [viewItem]);

  useEffect(() => {
    const fetchTrail = async () => {
      if (!trailItem?.id) {
        setViewTrail([]);
        return;
      }

      setViewTrailLoading(true);
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/requests/${trailItem.id}/trail`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) setViewTrail(json.data || []);
        else setViewTrail([]);
      } catch (err) {
        console.error("request trail", err);
        setViewTrail([]);
      } finally {
        setViewTrailLoading(false);
      }
    };

    fetchTrail();
  }, [trailItem]);

  useEffect(() => {
    if (!deletedRequestsModal) return;
    const fetchDeletedRequests = async () => {
      setDeletedRequestsLoading(true);
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/requests/deleted`, {
          credentials: "include",
        });
        const json = await res.json();
        setDeletedRequests(json.success ? json.data || [] : []);
      } catch (err) {
        console.error("deleted requests", err);
        setDeletedRequests([]);
      } finally {
        setDeletedRequestsLoading(false);
      }
    };
    fetchDeletedRequests();
  }, [deletedRequestsModal]);

  const filteredDeletedRequests = React.useMemo(() => {
    const term = deletedRequestsSearch.trim().toLowerCase();
    if (!term) return deletedRequests;
    return deletedRequests.filter((row) => {
      const payee = String(row.payee || "").toLowerCase();
      const rfp = String(row.rfp_no || "").toLowerCase();
      const deletedBy = String(row.deleted_by_name || "").toLowerCase();
      const jo = String(row.job_order || "").toLowerCase();
      return payee.includes(term) || rfp.includes(term) || deletedBy.includes(term) || jo.includes(term);
    });
  }, [deletedRequests, deletedRequestsSearch]);

  const deletedRequestsByPayee = React.useMemo(() => {
    const groups = new Map();
    filteredDeletedRequests.forEach((row) => {
      const key = row.payee || "Unknown payee";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [filteredDeletedRequests]);

  const selectedRows = data.filter((d) => selected.includes(d.id));
  const approverUserId = Number(user?.user_id || user?.id || 0);
  const canApprove = [127, 130].includes(approverUserId);
  const canEditBankFields =
    (editItem?.status || "").toLowerCase() === "approved";
  const normalizeRequestStatus = (value) =>
    String(value || "").toLowerCase() === "submitted" ? "pending" : String(value || "").toLowerCase();
  const getRequestStatusKey = (value) => normalizeRequestStatus(value).replace(/\s+/g, "_");
  const formatRequestStatusLabel = (value) =>
    normalizeRequestStatus(value).replace(/_/g, " ");
  const getRequestStatusBadgeClass = (value) => {
    const statusKey = getRequestStatusKey(value);

    if (statusKey === "approved") return "bg-green-100 text-green-700";
    if (statusKey === "cancelled") return "bg-red-100 text-red-700";
    if (statusKey === "pending") return "bg-blue-100 text-blue-700";
    if (statusKey.includes("partial")) return "bg-indigo-100 text-indigo-700";
    return "bg-gray-100 text-gray-600";
  };
  // Job orders come from a dedicated endpoint since `data` is now only the
  // current page of results, not every request the user can see.
  const uniqueJobOrders = React.useMemo(() => {
    // Include values returned by the filtered request search as well. This
    // keeps a searched request selectable even while the options endpoint is
    // refreshing or has not yet returned the newest value.
    const values = [
      ...jobOrderOptions,
      ...data.map((row) => String(row.job_order || "").trim()),
    ].filter(Boolean);

    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [jobOrderOptions, data]);
  const filteredJobOrders = React.useMemo(() => {
    const term = jobOrderSearchInput.trim().toLowerCase();
    if (!term) return uniqueJobOrders;
    return uniqueJobOrders.filter((jobOrder) => jobOrder.toLowerCase().includes(term));
  }, [jobOrderSearchInput, uniqueJobOrders]);
  const allJobOrders = React.useMemo(() => {
    const currentJobOrder = String(editItem?.job_order || "").trim();
    return Array.from(new Set([...jobOrderOptions, currentJobOrder].filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [jobOrderOptions, editItem?.job_order]);
  const filteredEditJobOrders = React.useMemo(() => {
    const term = editJobOrderSearch.trim().toLowerCase();
    if (!term) return allJobOrders;
    return allJobOrders.filter((jobOrder) => jobOrder.toLowerCase().includes(term));
  }, [allJobOrders, editJobOrderSearch]);

  useEffect(() => {
    const term = editJobOrderSearch.trim();
    if (!editItem || !canEditJobOrder || !term) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/requests/job-orders`);
        url.searchParams.set("scope", "all");
        url.searchParams.set("search", term);
        const response = await authFetch(url, { credentials: "include" });
        const json = await response.json();
        if (!cancelled && json.success) {
          const searchedJobOrders = (json.data || []).map((jobOrder) => String(jobOrder || "").trim()).filter(Boolean);
          setJobOrderOptions((current) =>
            Array.from(new Set([...current, ...searchedJobOrders])).sort((a, b) => a.localeCompare(b))
          );
        }
      } catch (error) {
        if (!cancelled) console.error("lookup edit job orders", error);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [editItem, editJobOrderSearch, canEditJobOrder]);

  const mainRows = data;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + mainRows.length;
  const filteredRequestCount = totalCount;
  const filteredRequestAmount = totalAmount;
  const requestsWithVoucherCount = voucherCounts.withVoucher;
  const requestsWithoutVoucherCount = voucherCounts.withoutVoucher;
  const selectableRequestIds = mainRows.filter((item) => item.can_create_check_voucher).map((item) => item.id);

  useEffect(() => {
    setSelected((prev) =>
      prev.filter((id) => mainRows.some((item) => item.id === id && item.can_create_check_voucher))
    );
  }, [mainRows]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    dateFilter,
    customStartDate,
    customEndDate,
    paymentFilter,
    joFilter,
    typeFilter,
    statusFilter,
    voucherFilter,
    scopeFilter,
    searchTerm,
  ]);

  const isCancelledTrailEvent = (event = {}) =>
    event?.event_type === "updated" &&
    normalizeRequestStatus(event?.after_state?.status) === "cancelled" &&
    normalizeRequestStatus(event?.before_state?.status) !== "cancelled";
  const formatTrailEvent = (eventType, event = null) => {
    if (event && isCancelledTrailEvent(event)) {
      return "Cancelled";
    }
    switch (eventType) {
      case "created":
        return "Created";
      case "updated":
        return "Edited";
      case "approved":
        return "Approved";
      case "returned":
        return "Returned";
      case "deleted":
        return "Deleted";
      case "cancelled":
        return "Cancelled";
      case "voucher_created":
        return "Voucher created";
      default:
        return eventType || "Updated";
    }
  };

  const getTrailDetails = (event) => {
    const before = event?.before_state || {};
    const after = event?.after_state || {};

    if (isCancelledTrailEvent(event)) {
      return [
        `Cancelled by ${event.actor_name || "System"}.`,
        after.status ? `Status set to ${after.status}.` : null,
        after.total_amount !== undefined
          ? `Amount updated to ₱${formatMoney(after.total_amount, "en-PH")}.`
          : null,
      ].filter(Boolean);
    }

    switch (event?.event_type) {
      case "created":
        return [
          `Created ${after.rfp_no || "request"} for ${after.payee || "—"}${after.job_order ? `, job order ${after.job_order}` : ""}.`,
          `Amount: ₱${formatMoney(after.total_amount, "en-PH")}.`,
          after.status ? `Initial status: ${after.status}.` : null,
        ].filter(Boolean);
      case "updated": {
        const lines = [];
        if (before.status !== after.status && after.status) {
          lines.push(`Status changed to ${after.status}.`);
        }
        if (before.mode_of_payment !== after.mode_of_payment && after.mode_of_payment) {
          lines.push(`Payment mode set to ${after.mode_of_payment}.`);
        }
        if (before.bank !== after.bank && after.bank) {
          lines.push(`Bank updated to ${after.bank}.`);
        }
        if (before.check_no !== after.check_no && after.check_no) {
          lines.push(`Check number updated to ${after.check_no}.`);
        }
        if (before.total_amount !== after.total_amount) {
          lines.push(`Amount updated to ₱${formatMoney(after.total_amount, "en-PH")}.`);
        }
        if (before.remarks !== after.remarks && after.remarks) {
          lines.push(`Remarks: ${after.remarks}.`);
        }
        if (before.return_reason !== after.return_reason && after.return_reason) {
          lines.push(`Return reason: ${after.return_reason}.`);
        }
        if (before.rush !== after.rush) {
          lines.push(after.rush ? "Marked as rush." : "Rush flag removed.");
        }
        if (before.for_liquidation !== after.for_liquidation) {
          lines.push(after.for_liquidation ? "Marked for liquidation." : "Removed from liquidation.");
        }
        if (before.currency !== after.currency && after.currency) {
          lines.push(`Currency set to ${after.currency}.`);
        }
        if (before.exchange_rate !== after.exchange_rate && after.exchange_rate) {
          lines.push(`Exchange rate set to ${after.exchange_rate}.`);
        }
        return lines.length ? lines : ["Request updated."];
      }
      case "approved":
        return [
          `Approved by ${event.actor_name || "System"}.`,
          after.status ? `Status set to ${after.status}.` : null,
          after.approved_at ? `Approved on ${new Date(after.approved_at).toLocaleString()}.` : null,
          after.for_liquidation !== undefined ? `For liquidation: ${after.for_liquidation ? "Yes" : "No"}.` : null,
        ].filter(Boolean);
      case "returned":
        return [
          `Returned to preparer by ${event.actor_name || "System"}.`,
          after.return_reason ? `Reason: ${after.return_reason}.` : null,
          after.status ? `Status set to ${after.status}.` : null,
        ].filter(Boolean);
      case "deleted":
        return [
          `Deleted by ${event.actor_name || "System"}.`,
        ];
      case "voucher_created":
        return [
          after.check_voucher_no ? `Linked to voucher ${after.check_voucher_no}.` : "Linked to a check voucher.",
        ];
      default:
        return [
          event?.summary || "Activity recorded.",
        ];
    }
  };

  const editDraftTotal = Number(editAmount || 0) * Number(editQty || 1);
  const editDraftTotalPhp = String(editItem?.currency || "PHP").toUpperCase() === "USD"
    ? editDraftTotal * (Number(editItem?.exchange_rate || 1) || 1)
    : editDraftTotal;
  const buildParticularsPreview = (particulars = [], targetTotal = 0) => {
    const originalGrandTotal = particulars.reduce((sum, p) => {
      const lineTotal = Number(p.total || Number(p.amount || 0) * Number(p.quantity || 1));
      return sum + lineTotal;
    }, 0);
    const scale = originalGrandTotal > 0 ? Number(targetTotal || 0) / originalGrandTotal : 1;

    return particulars.map((p) => {
      const originalTotal = Number(p.total || Number(p.amount || 0) * Number(p.quantity || 1));
      const previewTotal = Number((originalTotal * scale).toFixed(2));
      const previewQuantity = Number(p.quantity || 0);
      const previewUnitCost = previewQuantity ? Number((previewTotal / previewQuantity).toFixed(2)) : Number(p.amount || 0);
      const previewTaxRate = normalizeParticularTaxRate(p);
      const previewTax = previewTaxRate > 0
        ? Number(
            (
              (Number(p.tax_amount) || computeParticularTaxAmount(originalTotal, previewTaxRate)) *
              scale
            ).toFixed(2)
          )
        : 0;
      const previewNet = previewTaxRate > 0 ? Number((previewTotal - previewTax).toFixed(2)) : previewTotal;

      return {
        ...p,
        previewTaxRate,
        previewTotal,
        previewUnitCost,
        previewTax,
        previewNet,
      };
    });
  };

  const editParticularsPreview = buildParticularsPreview(editItem?.particulars || [], editDraftTotal);
  const editRequestCreatorId = Number(
    editItem?.preparer_user_id || editItem?.created_by_user_id || 0
  );
  const jobOrderEditorName = JOB_ORDER_EDITOR_NAME;
  const canApplyLateSignature = Boolean(
    editItem && currentUserId && editRequestCreatorId && currentUserId === editRequestCreatorId
  );
  const currentViewParticularsPreview = buildParticularsPreview(
    viewItem?.particulars || [],
    viewItem?.total_amount || 0
  );
  const activeParticularsPreview =
    viewItem && editItem && String(viewItem.id) === String(editItem.id)
      ? editParticularsPreview
      : currentViewParticularsPreview;
  const viewCreatedParts = viewItem?.created_at ? getManilaParts(viewItem.created_at) : null;
  const approvalCutoffBlocked = Boolean(
    viewItem &&
      viewCreatedParts &&
      !viewItem.rush &&
      viewCreatedParts.dateKey === todayManilaKey &&
      isAtOrAfterCutoff(viewCreatedParts)
  );

  const openVoucher = () => {
    if (!selectedRows.length) {
      setVoucherError("Select at least one request.");
      return;
    }

    if (selectedRows.some((row) => !row.can_create_check_voucher)) {
      setVoucherError("One or more selected requests are not available for check voucher creation yet.");
      return;
    }

    const hasUnapproved = selectedRows.some(
      (r) => (r.status || "").toLowerCase() !== "approved"
    );
    if (hasUnapproved) {
      setVoucherError("Only approved requests can be included in a check voucher.");
      return;
    }

    setVoucherError("");
    sessionStorage.setItem(
      "checkVoucherDraft",
      JSON.stringify({
        requests: selectedRows,
        assigned_checker_user_id: checkedByUserId || "",
      })
    );
    navigate("/accounting/check-voucher/create", {
      state: { requests: selectedRows, assigned_checker_user_id: checkedByUserId || "" },
    });
  };

  const generatePrint = (item, shipment) => {
    if (!item) return;
    // Get request-level currency and exchange rate
    const requestCurrency = String(item?.currency || "PHP").toUpperCase();
    const requestExchangeRate = Number(item?.exchange_rate || 1);
    const isUsd = requestCurrency === "USD" && requestExchangeRate > 0;
    const currencySymbol = isUsd ? "$" : "₱";

    let totalEwtAmountPhp = 0;

    const particularsHtml = (item.particulars || [])
      .map((p) => {
        // p.total and p.amount are stored in the original currency (USD if USD, PHP if PHP)
        const totalOriginal = Number(p.total || 0);
        const unitCostOriginal = Number(p.amount || 0);
        // Convert to PHP for display if USD
        const totalPhp = isUsd ? totalOriginal * requestExchangeRate : totalOriginal;
        const taxRate = normalizeParticularTaxRate(p);
        const taxAmountPhp = taxRate > 0 ? computeParticularTaxAmount(totalPhp, taxRate) : 0;
        const netAmountPhp = totalPhp - taxAmountPhp;
        // Calculate USD equivalents for tax and net (divide PHP amounts by exchange rate)
        const taxAmountUsd = isUsd && requestExchangeRate > 0 ? taxAmountPhp / requestExchangeRate : 0;
        const netAmountUsd = isUsd && requestExchangeRate > 0 ? netAmountPhp / requestExchangeRate : 0;
        const ewtAmountPhp = computeParticularEwtAmount(netAmountPhp, p.ewt_type, p.ewt_amount);
        const ewtAmountUsd = isUsd && requestExchangeRate > 0 ? ewtAmountPhp / requestExchangeRate : 0;
        totalEwtAmountPhp += ewtAmountPhp;
        // Show currency and conversion rate info if USD is used (same style as Qty/Unit Cost line)
        const currencyInfo = isUsd
          ? `<div><strong>Currency:</strong> ${requestCurrency} &nbsp; <strong>Conversion Rate:</strong> ${requestExchangeRate.toFixed(4)}</div>`
          : "";
        // For NET AMOUNT column: show dual amounts if USD
        const netDisplay = isUsd
          ? `<div>${currencySymbol}${formatMoney(netAmountUsd, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${formatMoney(netAmountPhp, "en-PH")}</div>`
          : `₱${formatMoney(netAmountPhp, "en-PH")}`;
        // For TAX column: show dual amounts if USD
        const taxDisplay = isUsd
          ? `<div>${currencySymbol}${formatMoney(taxAmountUsd, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${formatMoney(taxAmountPhp, "en-PH")}</div>`
          : `₱${formatMoney(taxAmountPhp, "en-PH")}`;
        // For EWT column: show dual amounts if USD
        const ewtDisplay = isUsd
          ? `<div>${currencySymbol}${formatMoney(ewtAmountUsd, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${formatMoney(ewtAmountPhp, "en-PH")}</div>`
          : `₱${formatMoney(ewtAmountPhp, "en-PH")}`;
        // For TOTAL column: show dual amounts if USD
        const totalDisplay = isUsd
          ? `<div>${currencySymbol}${formatMoney(totalOriginal, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${formatMoney(totalPhp, "en-PH")}</div>`
          : `₱${formatMoney(totalPhp, "en-PH")}`;
        return `<tr>
          <td style="padding:6px 8px; border:1px solid #000;">
            <div>${p.description || ""}</div>
            <div><strong>Qty:</strong> ${p.quantity || ""} &nbsp; <strong>Unit Cost:</strong> ${currencySymbol}${formatMoney(unitCostOriginal, "en-PH")}</div>
            ${currencyInfo}
          </td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${taxRate > 0 ? netDisplay : "—"}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${taxRate > 0 ? taxDisplay : "—"}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${ewtAmountPhp > 0 ? ewtDisplay : "—"}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right; font-weight:600;">${totalDisplay}</td>
        </tr>`;
      })
      .join("");

    const totalAmountPhp = getRequestTotalAmountPhp(item);
    const netPayableAmountPhp = Number((totalAmountPhp - totalEwtAmountPhp).toFixed(2));

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>Cash Voucher ${item.rfp_no || ""}</title>
      <style>
        *{box-sizing:border-box;} body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
        h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
        .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
        .row{display:flex;justify-content:space-between;gap:12px;margin-bottom:6px;}
        .block{flex:1;}
        .label-line{display:flex;gap:6px;align-items:center;margin-bottom:6px;}
        table{width:100%;border-collapse:collapse;margin-top:8px;}
        th,td{border:1px solid #000;padding:6px 8px;vertical-align:top;}
        th{text-align:left;}
        .tot{margin-top:6px;display:flex;justify-content:flex-end;}
        .tot .box{border:1px solid #000;padding:6px 12px;min-width:220px;}
        .sig-grid{display:flex;justify-content:space-between;margin-top:24px;width:100%;}
        .sig{text-align:center;flex:1;padding:0 10px;}
        .sig img{max-height:50px;max-width:120px;object-fit:contain;margin:0 auto;display:block;}
        .sig-line{border-top:1px solid #000;margin-top:8px;}
        .note{font-size:12px;margin-top:4px;}
      </style></head><body>
        <h1>CASH VOUCHER</h1>
        <div class="subhead">RFP No: ${item.rfp_no || ""}</div>
        <div style="margin-bottom:10px;">
          <div><strong>Date:</strong> ${item.created_at ? new Date(item.created_at).toLocaleDateString() : ""}</div>
          <div><strong>Payee:</strong> ${item.payee || ""}</div>
          <div><strong>Job Order:</strong> ${item.job_order || ""}</div>
          <div><strong>Corporate Client:</strong> ${shipment?.corporate_client || ""}</div>
          <div><strong>Shipment Description:</strong> ${shipment?.shipment_description || ""}</div>
        </div>
        ${
          item.remarks
            ? `<div style="margin-bottom:10px;"><strong>Remarks:</strong> ${item.remarks}</div>`
            : ""
        }

          <table>
          <thead>
            <tr>
              <th style="width:34%">PARTICULARS<br><small>Reference / Description / Quantity / Unit cost</small></th>
              <th style="width:14%; text-align:right;">NET AMOUNT</th>
              <th style="width:14%; text-align:right;">TAX</th>
              <th style="width:14%; text-align:right;">EWT</th>
              <th style="width:14%; text-align:right;">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${particularsHtml}
            <tr><td style="height:80px;"></td><td></td><td></td><td></td><td></td></tr>
          </tbody>
        </table>
            <div class="tot"><div class="box"><strong>TOTAL ${getAmountCurrencyMeta(item.currency).code}</strong> ${getRequestTotalHtml(totalAmountPhp, item.currency, item.exchange_rate)}</div></div>
        ${
          totalEwtAmountPhp > 0
            ? `
        <div class="tot"><div class="box"><strong>LESS: EWT</strong> (${getRequestTotalHtml(totalEwtAmountPhp, item.currency, item.exchange_rate)})</div></div>
        <div class="tot"><div class="box"><strong>TOTAL AMOUNT DUE</strong> ${getRequestTotalHtml(netPayableAmountPhp, item.currency, item.exchange_rate)}</div></div>
        `
            : ""
        }

        <div style="margin-top:10px;">
          <div><strong>Received from:</strong> _______________________</div>
          <div><strong>The amount of PESOS:</strong> __________________</div>
          <div class="note">In full payment of amount described above.</div>
        </div>

        <table class="sig-grid" style="width:100%;margin-top:24px;border:none;">
          <tr>
            <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
              ${item.preparer_signature_url ? `<img src="${item.preparer_signature_url}" style="max-height:50px;max-width:120px;">` : ""}
            </td>
            <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
              ${item.approver_signature_url ? `<img src="${item.approver_signature_url}" style="max-height:50px;max-width:120px;">` : ""}
            </td>
            <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
              ${item.receiver_signature_url ? `<img src="${item.receiver_signature_url}" style="max-height:50px;max-width:120px;">` : ""}
            </td>
          </tr>
          <tr>
            <td style="text-align:center;border:none;padding:0 10px;"><div style="border-top:1px solid #000;margin:8px 0;"></div>Prepared by: ${item.preparer_name || "____________________"}</td>
            <td style="text-align:center;border:none;padding:0 10px;"><div style="border-top:1px solid #000;margin:8px 0;"></div>Approved by (Final): ${item.approver_name || "____________________"}</td>
            <td style="text-align:center;border:none;padding:0 10px;"><div style="border-top:1px solid #000;margin:8px 0;"></div>Approved/Received by: ${item.receiver_name || "____________________"}</td>
          </tr>
        </table>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectableRequestIds.length && selectableRequestIds.every((id) => selected.includes(id))) {
      setSelected((prev) => prev.filter((id) => !selectableRequestIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...selectableRequestIds])));
    }
  };

  const getVoucherStateBadge = (request) => {
    const availableFromKey = getManilaDateKey(request?.check_voucher_available_from);
    const isAvailableLater = Boolean(availableFromKey && availableFromKey > todayManilaKey);
    const statusKey = getRequestStatusKey(request?.status);
    const hasVoucher = request.has_check_voucher || request.check_voucher_state === "voucher_created";

    if (statusKey === "cancelled") {
      return {
        label: hasVoucher
          ? request.check_voucher_no
            ? `Voucher cancelled: ${request.check_voucher_no}`
            : "Voucher cancelled"
          : "Cancelled",
        className: "bg-red-100 text-red-700",
      };
    }

    if (hasVoucher) {
      return {
        label: request.check_voucher_no ? `Voucher created: ${request.check_voucher_no}` : "Voucher created",
        className: "bg-emerald-100 text-emerald-700",
      };
    }

    if (request.check_voucher_state === "rush") {
      return {
        label: "Rush",
        className: "bg-emerald-100 text-emerald-700",
      };
    }

    if (statusKey.includes("partial")) {
      return {
        label: "Partially approved",
        className: "bg-indigo-100 text-indigo-700",
      };
    }

    if (request.check_voucher_state === "cutoff_pending") {
      return {
        label: isAvailableLater ? "Next day" : "Waiting for approval",
        className: "bg-amber-100 text-amber-700",
      };
    }

    if (request.can_create_check_voucher) {
      return {
        label: "Voucher ready",
        className: "bg-blue-100 text-blue-700",
      };
    }

    return {
      label: "Waiting for approval",
      className: "bg-amber-100 text-amber-700",
    };
  };

  const getVoucherDisabledTitle = (request) => {
    const availableFromKey = getManilaDateKey(request?.check_voucher_available_from);
    const isAvailableLater = Boolean(availableFromKey && availableFromKey > todayManilaKey);
    const statusKey = getRequestStatusKey(request?.status);
    const hasVoucher = request.has_check_voucher || request.check_voucher_state === "voucher_created";

    if (statusKey === "cancelled") {
      return hasVoucher
        ? request.check_voucher_no
          ? `This request was cancelled and ${request.check_voucher_no} is cancelled`
          : "This request and its linked check voucher are cancelled"
        : "This request is cancelled";
    }

    if (hasVoucher) {
      return request.check_voucher_no
        ? `This request already has ${request.check_voucher_no}`
        : "This request already has a check voucher";
    }

    if (request.check_voucher_state === "cutoff_pending") {
      return isAvailableLater
        ? "This request is past the cutoff and will be available next day"
        : "This request is waiting for approval";
    }

    if (statusKey.includes("partial")) {
      return "This request is partially approved and still waiting for final approval";
    }

    return "This request is waiting for approval";
  };

  const handleDelete = async (id) => {
    await authFetch(`${API_BASE_URL}/api/accounting/requests/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setSelected((prev) => prev.filter((x) => x !== id));
    setViewItem((current) => (current && String(current.id) === String(id) ? null : current));
    setDeleteId(null);
    load();
  };

  const handleCancelRequest = async () => {
    if (!cancelItem?.id) return;
    setCancelLoading(true);
    setCancelError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/requests/${cancelItem.id}/cancel`, {
        method: "POST",
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to cancel request");
      }
      setSelected((prev) => prev.filter((requestId) => !json.data?.cancelled_request_ids?.includes(requestId)));
      setCancelItem(null);
      if (viewItem && json.data?.cancelled_request_ids?.includes(viewItem.id)) {
        setViewItem(null);
      }
      load();
    } catch (err) {
      setCancelError(err.message || "Failed to cancel request");
    } finally {
      setCancelLoading(false);
    }
  };

  const openRequestView = (request) => {
    setViewItem(request);
  };

  const openRequestEdit = (request) => {
    setEditItem({ ...request });
    setEditJobOrderSearch("");
    const requestCurrency = String(request.currency || "PHP").toUpperCase();
    const requestRate = Number(request.exchange_rate || 1) || 1;
    const particularsOriginalTotal = (request.particulars || []).reduce(
      (sum, particular) => sum + Number(particular.amount || 0) * Number(particular.quantity || 1),
      0
    );
    setEditAmount(
      requestCurrency === "USD"
        ? particularsOriginalTotal || Number(request.total_amount || 0) / requestRate
        : Number(request.total_amount || 0)
    );
    setEditQty(1);
    setEditParticulars((request.particulars || []).map((particular) => ({ ...particular })));
    setEditAttachments([createAttachmentRow()]);
    setEditError("");
  };

  const handleResubmit = async (request) => {
    setResubmitLoadingId(request.id);
    await authFetch(`${API_BASE_URL}/api/accounting/requests/${request.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        status: "pending",
        remarks: "Resubmitted",
        return_reason: null,
      }),
    });
    setResubmitLoadingId(null);
    load();
  };

  const addEditAttachment = () => {
    setEditAttachments((current) => [...current, createAttachmentRow()]);
  };

  const updateEditAttachment = (id, file) => {
    setEditAttachments((current) =>
      current.map((row) => (row.id === id ? { ...row, file } : row))
    );
  };

  const removeEditAttachment = (id) => {
    setEditAttachments((current) => {
      const next = current.filter((row) => row.id !== id);
      return next.length ? next : [createAttachmentRow()];
    });
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    if (editItem.rush && !String(editItem.rush_reason || "").trim()) {
      setVoucherError("Rush requests must include a reason.");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      const total = Number(editAmount || 0) * Number(editQty || 1);
      const particularsPayload = editParticulars.map((particular) => {
        const quantity = Number(particular.quantity || 1) || 1;
        const amount = Number(particular.amount || 0);
        return {
          id: particular.id,
          amount,
          quantity,
          total: Number((amount * quantity).toFixed(2)),
          tax_type: particular.tax_type || null,
          tax_type_original: particular.tax_type || null,
          tax_amount: particular.tax_amount ?? null,
          tax_amount_original: particular.tax_amount ?? null,
          ewt_type: particular.ewt_type || null,
          ewt_type_original: particular.ewt_type || null,
          ewt_amount: particular.ewt_amount ?? null,
          ewt_amount_original: particular.ewt_amount ?? null,
        };
      });
      const payload = {
        status: editItem.status,
        remarks: editItem.remarks,
        job_order: editItem.job_order,
        mode_of_payment: editItem.mode_of_payment,
        currency: editItem.currency || "PHP",
        exchange_rate: Number(editItem.exchange_rate || 1),
        ...(canEditBankFields
          ? {
              bank: editItem.bank,
              check_no: editItem.check_no,
            }
          : {}),
        for_liquidation: editItem.for_liquidation,
        for_sales: editItem.type === "non_operational" && !!editItem.for_sales,
        rush: !!editItem.rush,
        rush_reason: editItem.rush ? editItem.rush_reason : null,
        total_amount:
          String(editItem.currency || "PHP").toUpperCase() === "USD"
            ? total * (Number(editItem.exchange_rate || 1) || 1)
            : total,
        particulars: particularsPayload,
        return_reason: editItem.return_reason,
        ...(editItem.preparer_signature_url !== undefined
          ? { preparer_signature_url: editItem.preparer_signature_url || null }
          : {}),
      };

      const hasEditAttachmentFiles = editAttachments.some((row) => row.file);
      const useMultipart = hasEditAttachmentFiles;
      const requestBody = useMultipart
        ? (() => {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
              if (key === "particulars") {
                formData.append(key, JSON.stringify(value));
              } else if (value !== undefined && value !== null) {
                formData.append(key, typeof value === "boolean" ? String(value) : String(value));
              }
            });
            editAttachments.forEach((row) => {
              if (row.file) {
                formData.append("files", row.file);
              }
            });
            return formData;
          })()
        : JSON.stringify(payload);

      const res = await authFetch(`${API_BASE_URL}/api/accounting/requests/${editItem.id}`, {
        method: "PUT",
        ...(useMultipart ? {} : { headers: { "Content-Type": "application/json" } }),
        credentials: "include",
        body: requestBody,
      });
      const json = await res.json();
      if (!json.success || !json.data) {
        throw new Error(json.message || "Failed to save request");
      }

      setViewItem((current) =>
        current && String(current.id) === String(json.data.id)
          ? { ...current, ...json.data }
          : current
      );
      setEditItem(null);
      setEditAttachments([createAttachmentRow()]);
      setEditAmount(0);
      setEditQty(1);
      load();
    } catch (err) {
      setEditError(err.message || "Failed to save request");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleApplyLateSignature = async () => {
    if (!editItem) return;
    const currentUserId = Number(user?.user_id || user?.id || 0);
    const preparerUserId = Number(editItem.preparer_user_id || editItem.created_by_user_id || 0);

    if (!currentUserId || currentUserId !== preparerUserId) {
      setEditError("Only the request creator can apply the preparer signature.");
      return;
    }

    setSignatureApplying(true);
    setEditError("");
    try {
      let signatureUrl = String(user?.signature_url || "").trim();

      if (!signatureUrl) {
        const response = await authFetch(`${API_BASE_URL}/api/auth/check-auth`, {
          credentials: "include",
          headers: {
            "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });
        const json = await response.json();
        signatureUrl = String(json?.user?.signature_url || "").trim();
      }

      if (!signatureUrl) {
        setSignatureFallbackOpen(true);
        return;
      }

      setEditItem((current) =>
        current ? { ...current, preparer_signature_url: signatureUrl } : current
      );
    } catch (err) {
      setEditError(err.message || "Failed to load your signature.");
    } finally {
      setSignatureApplying(false);
    }
  };

  return (
    <div className="space-y-4 font-poppins">
      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
            <h1 className="text-2xl font-semibold text-blue">Request Summary</h1>
            <p className="text-sm text-gray-600">
            Showing your requests (or all, if you are Accounting/Superadmin).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isRequestorPortal && (
              <>
                <button
                  type="button"
                  onClick={() => setScopeFilter("all")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    scopeFilter === "all"
                      ? "bg-blue text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setScopeFilter("created")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    scopeFilter === "created"
                      ? "bg-blue text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  Created by Me
                </button>
              </>
            )}
            <button
              onClick={load}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-gray-300"
            >
              Refresh
            </button>
          </div>
      </div>

      <div className="bg-white/95 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue">Requests ({totalCount})</h3>
            {loading && <span className="text-sm text-gray-500">Loading…</span>}
          </div>
          <div className="text-xs font-medium text-slate-500">
            Click any row to open the full request and available actions.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[260px] flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                ⌕
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search RFP no, payee, JO, bank, remarks..."
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
              />
            </div>
            <button
              onClick={() => setVoucherFilter("all")}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                voucherFilter === "all"
                  ? "bg-blue text-white shadow-sm"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All Requests
            </button>
            <button
              onClick={() => setVoucherFilter("with_voucher")}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                voucherFilter === "with_voucher"
                  ? "bg-blue text-white shadow-sm"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              With Voucher ({requestsWithVoucherCount})
            </button>
            <button
              onClick={() => setVoucherFilter("without_voucher")}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                voucherFilter === "without_voucher"
                  ? "bg-blue text-white shadow-sm"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Without Voucher ({requestsWithoutVoucherCount})
            </button>
            <button
              onClick={() => setDeletedRequestsModal(true)}
              className="rounded-full px-4 py-2 text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition inline-flex items-center gap-1.5"
            >
              <i className="fa-solid fa-trash-can text-xs" />
              Deleted Requests
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={dateFilter}
              onChange={(e) => {
                const nextValue = e.target.value;
                if (nextValue === "custom") {
                  previousDateFilterRef.current = dateFilter;
                  setDateFilter(previousDateFilterRef.current || "all");
                  openCustomDateModal();
                  return;
                }
                setDateFilter(nextValue);
              }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="all">All Payments</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="petty cash">Petty Cash</option>
              <option value="manager's check">Manager's Check</option>
              <option value="telegraphic transfer">Telegraphic Transfer</option>
              <option value="online banking transfer">Online Banking Transfer</option>
            </select>
            <div ref={jobOrderFilterRef} className="relative min-w-[220px]">
              <button
                type="button"
                onClick={() => setJobOrderMenuOpen((open) => !open)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-left flex items-center justify-between gap-2"
                aria-haspopup="listbox"
                aria-expanded={jobOrderMenuOpen}
              >
                <span className="truncate">
                  {joFilter === "all" ? `All Job Orders (${uniqueJobOrders.length})` : joFilter}
                </span>
                <i className={`fa-solid fa-chevron-${jobOrderMenuOpen ? "up" : "down"} text-xs text-gray-500`} />
              </button>
              {jobOrderMenuOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
                  <input
                    type="search"
                    value={jobOrderSearchInput}
                    onChange={(e) => setJobOrderSearchInput(e.target.value)}
                    placeholder="Search job order..."
                    className="w-full px-3 py-2 mb-2 text-sm border border-gray-200 rounded-md outline-none focus:border-blue-500"
                    autoFocus
                  />
                  {jobOrderLookupLoading && (
                    <p className="px-1 pb-2 text-xs text-gray-500">Searching requests...</p>
                  )}
                  <div className="max-h-64 overflow-y-auto" role="listbox" aria-label="Job Orders">
                    <button
                      type="button"
                      onClick={() => {
                        setJoFilter("all");
                        setJobOrderSearchInput("");
                        setJobOrderMenuOpen(false);
                      }}
                      className={`block w-full px-3 py-2 text-left text-sm rounded-md hover:bg-blue-50 ${joFilter === "all" ? "bg-blue-50 font-semibold text-blue-700" : "text-gray-700"}`}
                    >
                      All Job Orders ({uniqueJobOrders.length})
                    </button>
                    {filteredJobOrders.length ? (
                      filteredJobOrders.map((jo) => (
                        <button
                          key={jo}
                          type="button"
                          onClick={() => {
                            setJoFilter(jo);
                            setJobOrderSearchInput("");
                            setJobOrderMenuOpen(false);
                          }}
                          className={`block w-full px-3 py-2 text-left text-sm rounded-md hover:bg-blue-50 ${joFilter === jo ? "bg-blue-50 font-semibold text-blue-700" : "text-gray-700"}`}
                        >
                          {jo}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-500">No matching Job Orders</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="all">All Types</option>
              <option value="operational">Operational</option>
              <option value="non_operational">Non-Operational</option>
              <option value="sales">Sales</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="partially_approved">Partially Approved</option>
              <option value="approved">Approved</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button
              onClick={exportToExcel}
              disabled={!totalCount || exportLoading}
              className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              <i className={`fa-solid ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"} mr-1`}></i>
              {exportLoading ? "Exporting..." : "Export Excel"}
            </button>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                Checked by
              </label>
              <select
                value={checkedByUserId}
                onChange={(e) => setCheckedByUserId(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-[220px]"
              >
                <option value="">Select checked by</option>
                {checkedByUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.user_name || ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={openVoucher}
              disabled={!selectedRows.length || selectedRows.some((row) => !row.can_create_check_voucher)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title={
                !selectedRows.length
                  ? "Select requests first"
                  : selectedRows.some((row) => !row.can_create_check_voucher)
                    ? "One or more selected requests are not available for check voucher creation"
                  : "Create check voucher"
              }
            >
              Create Check Voucher
            </button>
          </div>
        </div>
        <div className="px-5 pb-3">
          <div className="text-sm text-gray-600">
            <span className="font-semibold text-slate-600">Active Date Range:</span>{" "}
            <span className="text-blue">{getActiveDateRangeLabel()}</span>
          </div>
        </div>
        {customDateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-blue">Custom Date Range</h2>
                <p className="text-sm text-grey">Choose the start and end date for the request list.</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs text-grey mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customDateDraftStart}
                    onChange={(e) => setCustomDateDraftStart(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-grey mb-1">End Date</label>
                  <input
                    type="date"
                    value={customDateDraftEnd}
                    onChange={(e) => setCustomDateDraftEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={closeCustomDateModal}
                  className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-grey transition hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCustomDateRange}
                  disabled={!customDateDraftStart || !customDateDraftEnd}
                  className="inline-flex items-center justify-center rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="px-5 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Total Requests</p>
              <p className="mt-1 text-2xl font-bold text-blue-900">{filteredRequestCount}</p>
              <p className="text-xs text-blue-700">Based on the selected date and payment filters</p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Total Amount</p>
              <p className="mt-1 text-2xl font-bold text-amber-900">₱{formatMoney(filteredRequestAmount, "en-PH")}</p>
              <p className="text-xs text-amber-700">Based on the selected date and payment filters</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1600px] text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                <th className="px-5 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={selectableRequestIds.length > 0 && selectableRequestIds.every((id) => selected.includes(id))}
                    disabled={!selectableRequestIds.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-5 py-2 w-[240px] min-w-[240px]">RFP No</th>
                <th className="px-5 py-2">Status</th>
                <th className="px-5 py-2">Type</th>
                <th className="px-5 py-2">Payee</th>
                <th className="px-5 py-2">Payee Type</th>
                <th className="px-5 py-2">Job Order</th>
                <th className="px-5 py-2">Currency</th>
                <th className="px-5 py-2">Conversion Rate</th>
                <th className="px-5 py-2">Mode of Payment</th>
                <th className="px-5 py-2">Bank</th>
                <th className="px-5 py-2">Check No.</th>
                <th className="px-5 py-2">For Liquidation</th>
                <th className="px-5 py-2 w-[120px] min-w-[120px]">Tax</th>
                <th className="px-5 py-2">Preparer</th>
                <th className="px-5 py-2">Remarks</th>
                <th className="px-5 py-2">Created</th>
                <th className="px-5 py-2">Due Date</th>
                <th className="px-5 py-2 text-right">Total</th>
                <th className="px-5 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mainRows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => openRequestView(r)}
                >
                  <td className="px-5 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(r.id)}
                      disabled={!r.can_create_check_voucher}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(r.id)}
                      title={r.can_create_check_voucher ? "" : getVoucherDisabledTitle(r)}
                    />
                  </td>
                  
                  <td className="px-5 py-3 font-semibold text-gray-800 w-[240px] min-w-[240px]">
                    <div className="flex flex-col gap-1">
                      <span>{r.rfp_no || "—"}</span>
                      <span
                        className={`inline-flex w-fit whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${getVoucherStateBadge(r).className}`}
                      >
                        {getVoucherStateBadge(r).label}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getRequestStatusBadgeClass(r.status)}`}
                    >
                      {formatRequestStatusLabel(r.status) || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium text-gray-800">{r.type}</td>
                  <td className="px-5 py-3 text-gray-700">{r.payee || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">{r.payee_type || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">{r.job_order || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">{String(r.currency || "PHP").toUpperCase()}</td>
                  <td className="px-5 py-3 text-gray-700">{Number(r.exchange_rate || 1).toFixed(4)}</td>
                  <td className="px-5 py-3 text-gray-700">{r.mode_of_payment || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">{r.bank || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">{r.check_no || "—"}</td>
                  <td className="px-5 py-3 text-gray-700">
                    {r.for_liquidation ? "Yes" : "No"}
                  </td>
                  <td className="px-5 py-3">
                    {(r.particulars || []).some((p) => p.with_tax) ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Tax Applied
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        No Tax
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.preparer_name || "—"}</td>
                 
                  <td className="px-5 py-3 text-gray-700 max-w-xs truncate" title={r.remarks || ""}>
                    {r.status === "returned" && r.return_reason ? (
                      <span className="text-red-600 font-semibold">
                        Return: {r.return_reason}
                      </span>
                    ) : (
                      r.remarks ||
                      (normalizeRequestStatus(r.status) === "pending" && r.return_reason ? "Resubmitted" : "—")
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-800">
                    {renderRequestTotal(getRequestNetPayableAmount(r), r.currency, r.exchange_rate)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:text-blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRequestView(r);
                      }}
                    >
                      <i className="fa-solid fa-folder-open text-[11px]" />
                      Open
                    </button>
                  </td>
                </tr>
              ))}
              {!data.length && (
                <tr>
                  <td className="px-5 py-5 text-center text-gray-500" colSpan={18}>
                    {totalCount === 0 ? "No requests found." : "No requests for selected filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Rows per page:</label>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span className="text-sm text-gray-600">
                Showing {totalCount ? startIndex + 1 : 0}-{Math.min(endIndex, totalCount)} of {totalCount} requests
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <i className="fa-solid fa-angles-left" />
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <i className="fa-solid fa-angle-left" />
              </button>

              <div className="flex items-center gap-1 mx-2">
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="start-ellipsis" className="px-1 text-gray-400">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 text-sm border rounded-lg ${
                          currentPage === i
                            ? "bg-blue text-white border-blue"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="end-ellipsis" className="px-1 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <i className="fa-solid fa-angle-right" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <i className="fa-solid fa-angles-right" />
              </button>
            </div>
          </div>
        )}
      </div>

      {cutoffPendingRows.length > 0 && (
        <div className="mt-6 bg-white/95 rounded-3xl border border-amber-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
            <h3 className="font-semibold text-amber-900">Cutoff Pending Requests</h3>
            <p className="text-sm text-amber-800">
              Approved requests created or approved after 4:00 PM are available for check voucher creation on the next day unless marked as rush.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-amber-50 text-amber-900">
                <tr className="text-left uppercase text-[11px] tracking-wide text-amber-700">
                  <th className="px-4 py-2 w-[220px] min-w-[220px]">RFP No</th>
                  <th className="px-4 py-2">Payee</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Approved</th>
                  <th className="px-4 py-2">Available On</th>
                  <th className="px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {cutoffPendingRows.map((r) => (
                  <tr key={r.id} className="bg-white">
                    <td className="px-4 py-3 font-semibold text-gray-800 w-[220px] min-w-[220px]">
                      <div className="flex flex-col gap-1">
                        <span>{r.rfp_no || "—"}</span>
                        <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          Next day
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.payee || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.approved_at ? new Date(r.approved_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {formatDateKey(r.check_voucher_available_from)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">
                      {r.check_voucher_reason || "Approved after cutoff"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    {editItem &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setEditItem(null);
                setEditAttachments([createAttachmentRow()]);
                setEditError("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-blue mb-4">Edit Request</h3>
            {editError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {editError}
              </div>
            )}
            <div className="space-y-3 text-sm">
              {![127, 130].includes(Number(user?.user_id || user?.id)) && (
                <div>
                  <label className="font-medium text-gray-700">Status</label>
                  <select
                    value={editItem.status}
                    onChange={(e) => setEditItem({ ...editItem, status: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-gray-700 text-sm">
                <div>
                  <p className="font-medium text-gray-600">Type</p>
                  <p className="mt-1 text-gray-900">{editItem.type || "—"}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">Job Order</p>
                  {canEditJobOrder ? (
                    <div className="relative mt-1">
                      <input
                        type="search"
                        value={editJobOrderSearch}
                        onChange={(e) => setEditJobOrderSearch(e.target.value)}
                        placeholder={editItem.job_order || "Search Job Order..."}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-blue-500"
                        aria-label="Search Job Order"
                      />
                      <div className="mt-1 flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
                        <span className="font-semibold">Selected: {editItem.job_order || "None"}</span>
                        <span>{filteredEditJobOrders.length} available</span>
                      </div>
                      <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                        {filteredEditJobOrders.length ? (
                          filteredEditJobOrders.map((jobOrder) => (
                            <button
                              key={jobOrder}
                              type="button"
                              onClick={() => {
                                setEditItem({ ...editItem, job_order: jobOrder });
                                setEditJobOrderSearch("");
                              }}
                              className={`block w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                                editItem.job_order === jobOrder ? "bg-blue-50 font-semibold text-blue-700" : "text-gray-700"
                              }`}
                            >
                              {jobOrder}
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-2 text-sm text-gray-500">No matching Job Orders</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <input
                      value={editItem.job_order || ""}
                      readOnly
                      className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600"
                    />
                  )}
                  <p className="mt-1 text-xs text-amber-700">
                    Note: Only {jobOrderEditorName} can edit Job Order.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">Payee</p>
                  <p className="mt-1 text-gray-900">{editItem.payee || "—"}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">Payee Type</p>
                  <p className="mt-1 text-gray-900">{editItem.payee_type || "—"}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Currency</label>
                  <input
                    value={editItem.currency || "PHP"}
                    onChange={(e) => setEditItem({ ...editItem, currency: e.target.value.toUpperCase() })}
                    className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 uppercase"
                    placeholder="PHP"
                  />
                </div>
                <div>
                  <label className="font-medium text-gray-600">Conversion Rate</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={editItem.exchange_rate ?? 1}
                    onChange={(e) =>
                      setEditItem({
                        ...editItem,
                        exchange_rate: e.target.value === "" ? "" : Number(e.target.value),
                      })
                    }
                    className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                    placeholder="1.0000"
                  />
                </div>
              </div>
              <div>
                <label className="font-medium text-gray-700">Mode of Payment</label>
                <select
                  value={editItem.mode_of_payment || ""}
                  onChange={(e) => setEditItem({ ...editItem, mode_of_payment: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 bg-white"
                >
                  <option value="">Select</option>
                  {paymentModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>
              {canEditBankFields && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-medium text-gray-700">Bank</label>
                    <input
                      value={editItem.bank || ""}
                      onChange={(e) => setEditItem({ ...editItem, bank: e.target.value })}
                      className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div>
                    <label className="font-medium text-gray-700">Check No.</label>
                    <input
                      value={editItem.check_no || ""}
                      onChange={(e) => setEditItem({ ...editItem, check_no: e.target.value })}
                      className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                      placeholder="Enter check number"
                    />
                  </div>
                </div>
              )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-medium text-gray-700">Amount</label>
                    <input
                      type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(Number(e.target.value || 0))}
                    className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="font-medium text-gray-700">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={editQty}
                    onChange={(e) => setEditQty(Number(e.target.value || 1))}
                    className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mt-1">
                    <span className="text-gray-700 font-medium">Total</span>
                    <div className="text-blue font-semibold">
                      {renderRequestTotal(editDraftTotalPhp, editItem.currency, editItem.exchange_rate)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-gray-800">Particulars breakdown</p>
                    <p className="text-xs text-gray-500">Edit tax status and amount for each line item.</p>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    {editParticulars.length} item(s)
                  </span>
                </div>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border border-gray-200 px-3 py-2 text-left">Description</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Qty</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Unit Cost</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Input Tax</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">EWT</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editParticulars.map((p, idx) => {
                        const qty = Number(p.quantity || 1) || 1;
                        const amount = Number(p.amount || 0);
                        const lineTotal = amount * qty;
                        const taxType = p.tax_type || "";
                        const ewtType = p.ewt_type || "";
                        return (
                          <tr key={p.id || `${p.description}-${p.amount}-${p.quantity}`}>
                            <td className="border border-gray-200 px-3 py-2">{p.description || "—"}</td>
                            <td className="border border-gray-200 px-3 py-2 text-right">{qty}</td>
                            <td className="border border-gray-200 px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => {
                                  const newAmount = Number(e.target.value) || 0;
                                  setEditParticulars((prev) =>
                                    prev.map((item, i) =>
                                      i === idx ? { ...item, amount: newAmount } : item
                                    )
                                  );
                                }}
                                className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                              />
                            </td>
                            <td className="border border-gray-200 px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <select
                                  value={taxType}
                                  onChange={(e) => {
                                    const nextType = e.target.value;
                                    setEditParticulars((prev) =>
                                      prev.map((item, i) =>
                                        i === idx ? { ...item, tax_type: nextType || null } : item
                                      )
                                    );
                                  }}
                                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                                >
                                  {TAX_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {taxType === "specific_amount" && (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={p.tax_amount ?? ""}
                                    onChange={(e) => {
                                      const nextAmount = e.target.value;
                                      setEditParticulars((prev) =>
                                        prev.map((item, i) =>
                                          i === idx ? { ...item, tax_amount: nextAmount } : item
                                        )
                                      );
                                    }}
                                    className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                                    placeholder="Amount"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="border border-gray-200 px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <select
                                  value={ewtType}
                                  onChange={(e) => {
                                    const nextType = e.target.value;
                                    setEditParticulars((prev) =>
                                      prev.map((item, i) =>
                                        i === idx ? { ...item, ewt_type: nextType || null } : item
                                      )
                                    );
                                  }}
                                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                                >
                                  {EWT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {ewtType === "specific_amount" && (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={p.ewt_amount ?? ""}
                                    onChange={(e) => {
                                      const nextAmount = e.target.value;
                                      setEditParticulars((prev) =>
                                        prev.map((item, i) =>
                                          i === idx ? { ...item, ewt_amount: nextAmount } : item
                                        )
                                      );
                                    }}
                                    className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                                    placeholder="Amount"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="border border-gray-200 px-3 py-2 text-right font-medium">₱{formatMoney(lineTotal, "en-PH")}</td>
                          </tr>
                        );
                      })}
                      {!editParticulars.length && (
                        <tr>
                          <td className="border border-gray-200 px-3 py-3 text-center text-gray-500" colSpan={6}>
                            No particulars found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {editParticulars.length > 0 && (() => {
                  const totalSales = editParticulars.reduce(
                    (sum, p) => sum + Number(p.amount || 0) * Number(p.quantity || 1),
                    0
                  );
                  const totalTax = editParticulars.reduce((sum, p) => {
                    const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
                    return sum + computeParticularTaxAmountByType(lineTotal, p.tax_type, p.tax_amount);
                  }, 0);
                  const totalNetOfTax = editParticulars.reduce((sum, p) => {
                    const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
                    const taxAmount = computeParticularTaxAmountByType(lineTotal, p.tax_type, p.tax_amount);
                    return sum + (lineTotal - taxAmount);
                  }, 0);
                  const totalEwt = editParticulars.reduce((sum, p) => {
                    const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
                    const taxAmount = computeParticularTaxAmountByType(lineTotal, p.tax_type, p.tax_amount);
                    return sum + computeParticularEwtAmount(lineTotal - taxAmount, p.ewt_type, p.ewt_amount);
                  }, 0);
                  const totalDue = Number((totalSales - totalEwt).toFixed(2));
                  return (
                    <div className="mt-3 flex justify-end">
                      <div className="w-full sm:w-80 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
                        <div className="flex items-center justify-between py-1">
                          <span className="text-gray-600">Total (VAT Inclusive)</span>
                          <span className="font-medium text-gray-800">₱{formatMoney(totalSales, "en-PH")}</span>
                        </div>
                        {totalTax > 0 && (
                          <div className="flex items-center justify-between py-1 text-xs text-gray-500">
                            <span>Input Tax (included)</span>
                            <span>₱{formatMoney(totalTax, "en-PH")}</span>
                          </div>
                        )}
                        {totalTax > 0 && (
                          <div className="flex items-center justify-between py-1 text-xs text-gray-500">
                            <span>Net Amount (VAT Exclusive)</span>
                            <span>₱{formatMoney(totalNetOfTax, "en-PH")}</span>
                          </div>
                        )}
                        {totalEwt > 0 && (
                          <div className="flex items-center justify-between py-1">
                            <span className="text-gray-600">Less: EWT</span>
                            <span className="font-medium text-gray-800">(₱{formatMoney(totalEwt, "en-PH")})</span>
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2">
                          <span className="font-semibold text-gray-700">Total Amount Due</span>
                          <span className="text-base font-bold text-blue">₱{formatMoney(totalDue, "en-PH")}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {![127, 130].includes(Number(user?.user_id || user?.id)) && (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editItem.for_liquidation}
                      onChange={(e) =>
                        setEditItem({ ...editItem, for_liquidation: e.target.checked })
                      }
                    />
                    <span className="text-gray-700">For liquidation</span>
                  </div>
                  {editItem.type === "non_operational" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editItem.for_sales}
                        onChange={(e) =>
                          setEditItem({ ...editItem, for_sales: e.target.checked })
                        }
                      />
                      <span className="text-gray-700">For Sales</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editItem.rush}
                      onChange={(e) =>
                        setEditItem({ ...editItem, rush: e.target.checked })
                      }
                    />
                    <span className="text-gray-700">Rush request</span>
                  </div>
                  {editItem.rush && (
                    <div>
                      <label className="font-medium text-gray-700">Rush reason</label>
                      <textarea
                        value={editItem.rush_reason || ""}
                        onChange={(e) => setEditItem({ ...editItem, rush_reason: e.target.value })}
                        className="w-full mt-1 rounded-lg border border-amber-200 px-3 py-2 min-h-[80px]"
                        placeholder="Explain why this request should bypass the cutoff"
                      />
                    </div>
                  )}
                  <div>
                    <label className="font-medium text-gray-700">Return reason</label>
                    <textarea
                      value={editItem.return_reason || ""}
                      onChange={(e) => setEditItem({ ...editItem, return_reason: e.target.value })}
                      className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 min-h-[80px]"
                      placeholder="Optional: add or clear the return reason"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="font-medium text-gray-700">Remarks</label>
                <textarea
                  value={editItem.remarks || ""}
                  onChange={(e) => setEditItem({ ...editItem, remarks: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 min-h-[80px]"
                />
              </div>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-blue-900">Preparer signature</p>
                    <p className="text-xs text-blue-700">
                      Apply the request creator&apos;s uploaded signature to this request snapshot.
                    </p>
                  </div>
                  {canApplyLateSignature ? (
                    <button
                      type="button"
                      onClick={handleApplyLateSignature}
                      disabled={signatureApplying}
                      className="inline-flex items-center gap-2 rounded-xl bg-blue px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className={`fa-solid ${signatureApplying ? "fa-spinner fa-spin" : "fa-signature"}`} />
                      {signatureApplying ? "Applying..." : "Apply my signature"}
                    </button>
                  ) : (
                    <span className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-500 ring-1 ring-blue-100">
                      Creator only
                    </span>
                  )}
                </div>
                <div className="mt-3 rounded-xl border border-blue-100 bg-white px-3 py-3">
                  {editItem.preparer_signature_url ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">Signature attached</p>
                        <p className="text-xs text-gray-500 truncate">
                          {editItem.preparer_signature_url}
                        </p>
                      </div>
                      <img
                        src={editItem.preparer_signature_url}
                        alt="Preparer signature"
                        className="max-h-14 max-w-[140px] object-contain"
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {canApplyLateSignature
                        ? "No preparer signature attached yet. Upload it in your profile, then click the button above."
                        : "No preparer signature attached yet."}
                    </p>
                  )}
                </div>
              </div>
              {signatureFallbackOpen &&
                ReactDOM.createPortal(
                  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                          <i className="fa-solid fa-triangle-exclamation" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-lg font-semibold text-gray-900">Signature not found</h4>
                          <p className="mt-1 text-sm text-gray-600">
                            Your profile does not have a saved signature yet. Upload your signature first, then come back and apply it here.
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button
                          type="button"
                          onClick={() => setSignatureFallbackOpen(false)}
                          className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Close
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSignatureFallbackOpen(false);
                            navigate(
                              location.pathname.startsWith("/requestor")
                                ? "/requestor/profile"
                                : "/accounting/profile"
                            );
                          }}
                          className="rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue/90"
                        >
                          Open Profile
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800">Attachments</p>
                    <p className="text-xs text-gray-500">
                      Existing attachments are shown below. You can add more documents before saving.
                    </p>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    {(editItem?.attachments || []).length + editAttachments.filter((row) => row.file).length} file(s)
                  </span>
                </div>
                {(editItem?.attachments || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {(editItem.attachments || []).map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.file_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-gray-800">{doc.file_name || "Attachment"}</p>
                          <p className="text-xs text-gray-500">
                            {(doc.file_type || "file").toString()} •{" "}
                            {doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-blue">Open</span>
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-3 space-y-2">
                  {editAttachments.map((row, index) => (
                    <div
                      key={row.id}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                    >
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => updateEditAttachment(row.id, e.target.files?.[0] || null)}
                        className="block w-full text-sm"
                      />
                      {row.file ? (
                        <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                          {row.file.name}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => removeEditAttachment(row.id)}
                        className="text-xs font-semibold text-red hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={addEditAttachment}
                    className="inline-flex items-center gap-2 rounded-xl border border-blue/20 bg-blue/5 px-3 py-2 text-sm font-semibold text-blue hover:bg-blue/10"
                  >
                    <i className="fa-solid fa-plus" />
                    Add attachment
                  </button>
                  {editAttachments.some((row) => row.file) ? (
                    <div className="text-xs text-gray-500">
                      {editAttachments.filter((row) => row.file).length} ready to upload
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditItem(null);
                  setEditAttachments([createAttachmentRow()]);
                  setEditError("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 disabled:opacity-60"
              >
                {savingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {cancelItem &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                if (cancelLoading) return;
                setCancelItem(null);
                setCancelError("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-amber-700 mb-2">Cancel Request</h3>
            <p className="text-sm text-gray-700 mb-2">
              This will set the selected request to cancelled and turn its amount to 0.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              If it is connected to a check voucher, all requests linked to that voucher and the voucher amount will also be set to 0. This action will be audited.
            </p>
            {cancelError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {cancelError}
              </div>
            )}
            <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
              <div><strong>RFP No:</strong> {cancelItem.rfp_no || "—"}</div>
              <div><strong>Payee:</strong> {cancelItem.payee || "—"}</div>
              <div><strong>Job Order:</strong> {cancelItem.job_order || "—"}</div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  if (cancelLoading) return;
                  setCancelItem(null);
                  setCancelError("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                Keep Request
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelLoading}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60"
              >
                {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {deleteId &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative">
            <button
              onClick={() => setDeleteId(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-red mb-2">Delete Request</h3>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete this request? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="px-4 py-2 rounded-lg bg-red text-white text-sm font-semibold hover:bg-red/90"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {voucherError &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setVoucherError("")}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-red-600 mb-2">Voucher Error</h3>
            <p className="text-sm text-gray-700">
              {voucherError}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setVoucherError("")}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {returnOpen &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setReturnOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-red mb-2">Return to Preparer</h3>
            <p className="text-sm text-gray-700 mb-3">
              Provide a reason for returning this request to the preparer.
            </p>
            <textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[120px]"
              placeholder="Enter reason"
            />
            {returnError && (
              <p className="text-xs text-red-600 mt-2">{returnError}</p>
            )}
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setReturnOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!returnReason.trim()) {
                    setReturnError("Reason is required");
                    return;
                  }
                  setReturnError("");
                  setReturnLoading(true);
                  await authFetch(`${API_BASE_URL}/api/accounting/requests/${viewItem.id}/return`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ reason: returnReason }),
                  });
                  setReturnLoading(false);
                  setReturnOpen(false);
                  setViewItem(null);
                  load();
                }}
                disabled={returnLoading}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {returnLoading ? "Returning..." : "Return"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {trailItem &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setTrailItem(null);
                setViewTrail([]);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>

            <h3 className="text-lg font-semibold text-blue mb-1">Request Trail</h3>
            <p className="text-sm text-gray-600 mb-4">
              {trailItem.rfp_no || "—"} {trailItem.payee ? `- ${trailItem.payee}` : ""}
            </p>

            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                {viewTrailLoading ? "Loading..." : `${viewTrail.length} event(s)`}
              </div>
            </div>

            {viewTrail.length ? (
              <div className="space-y-3">
                {viewTrail.map((event) => (
                    <div key={event.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                            isCancelledTrailEvent(event)
                              ? "bg-red-100 text-red-700"
                              : event.event_type === "created"
                              ? "bg-blue-100 text-blue-700"
                              : event.event_type === "approved"
                                ? "bg-emerald-100 text-emerald-700"
                              : event.event_type === "returned"
                                ? "bg-amber-100 text-amber-700"
                                  : event.event_type === "deleted"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {formatTrailEvent(event.event_type, event)}
                        </span>
                        <span className="font-semibold text-gray-800">
                          {event.actor_name || "System"}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {event.created_at ? new Date(event.created_at).toLocaleString() : "—"}
                      </span>
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-gray-700">
                      {getTrailDetails(event).map((line, index) => (
                        <div key={`${event.id}-detail-${index}`}>{line}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-500">
                No audit trail available for this request yet.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

    {deletedRequestsModal &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-6 relative max-h-[90vh] flex flex-col overflow-hidden">
            <button
              onClick={() => {
                setDeletedRequestsModal(false);
                setDeletedRequestsSearch("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>

            <h3 className="text-lg font-semibold text-red-700 mb-1">Deleted Requests</h3>
            <p className="text-sm text-gray-600 mb-4">
              Permanently deleted requests, grouped by payee, sourced from the audit trail.
            </p>

            <div className="mb-4">
              <input
                type="text"
                value={deletedRequestsSearch}
                onChange={(e) => setDeletedRequestsSearch(e.target.value)}
                placeholder="Search payee, RFP no, job order, or deleted by..."
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {deletedRequestsLoading ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                  Loading deleted requests...
                </div>
              ) : deletedRequestsByPayee.length ? (
                <div className="space-y-4">
                  {deletedRequestsByPayee.map(([payee, rows]) => (
                    <div key={payee} className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between gap-2 bg-red-50 px-4 py-2">
                        <span className="font-semibold text-gray-800">{payee}</span>
                        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                          {rows.length} deleted
                        </span>
                      </div>
                      <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="border-b border-gray-200 px-3 py-2 text-left">RFP No</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left">Type</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-right">Amount</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left">Deleted By</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left">Deleted At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr key={row.audit_id}>
                              <td className="border-b border-gray-100 px-3 py-2">{row.rfp_no || "—"}</td>
                              <td className="border-b border-gray-100 px-3 py-2 capitalize">
                                {(row.type || "—").replace("_", " ")}
                              </td>
                              <td className="border-b border-gray-100 px-3 py-2 text-right">
                                {row.total_amount != null
                                  ? `${row.currency === "USD" ? "$" : "₱"}${formatMoney(row.total_amount, row.currency === "USD" ? "en-US" : "en-PH")}`
                                  : "—"}
                              </td>
                              <td className="border-b border-gray-100 px-3 py-2">{row.deleted_by_name || "—"}</td>
                              <td className="border-b border-gray-100 px-3 py-2">
                                {row.deleted_at ? new Date(row.deleted_at).toLocaleString() : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                  No deleted requests found.
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    {viewItem &&
      ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-8 relative my-auto max-h-[90vh] overflow-y-auto" style={{ fontFamily: '"Times New Roman", serif' }}>
            <button
              onClick={() => setViewItem(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>

            {/* Header */}
            <h1 className="text-2xl font-bold text-center tracking-widest mb-1">CASH VOUCHER</h1>
            <p className="text-center font-semibold mb-4">RFP No: {viewItem.rfp_no || "—"}</p>
            {viewItem.return_reason && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 mb-4">
                <div className="text-sm font-semibold">Return reason</div>
                <div className="text-sm whitespace-pre-wrap">{viewItem.return_reason}</div>
              </div>
            )}

            {/* Info Section */}
            <div className="mb-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><strong>Date:</strong> {viewItem.created_at ? new Date(viewItem.created_at).toLocaleDateString() : "—"}</div>
              <div><strong>Payee:</strong> {viewItem.payee || "—"}</div>
              <div><strong>Job Order:</strong> {viewItem.job_order || "—"}</div>
              <div><strong>Corporate Client:</strong> {viewShipment?.corporate_client || "—"}</div>
              <div className="sm:col-span-2"><strong>Shipment Description:</strong> {viewShipment?.shipment_description || "—"}</div>
            </div>

            {viewItem.remarks && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <strong>Remarks:</strong> {viewItem.remarks}
              </div>
            )}

            {(viewItem.attachments || []).length > 0 && (
              <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800">Attached documents</p>
                    <p className="text-xs text-gray-500">
                      Files uploaded with this request. Review them before approving.
                    </p>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    {viewItem.attachments.length} file(s)
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  {viewItem.attachments.map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.file_url || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-800">{doc.file_name || "Attachment"}</p>
                        <p className="text-xs text-gray-500">
                          {(doc.file_type || "file").toString()} •{" "}
                          {doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-blue">Open</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Particulars Table */}
            <table className="w-full border-collapse text-sm mb-4">
              <thead>
                <tr>
                  <th className="border border-gray-800 px-3 py-2 text-left w-[40%]">
                    PARTICULARS
                    <br />
                    <small className="font-normal text-gray-600">Reference / Description / Quantity / Unit cost</small>
                  </th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">NET AMOUNT</th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">TAX</th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">EWT</th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-[14%]">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {activeParticularsPreview.map((p) => {
                  // Get currency info from viewItem
                  const itemCurrency = String(viewItem?.currency || "PHP").toUpperCase();
                  const itemExchangeRate = Number(viewItem?.exchange_rate || 1);
                  const isUsdItem = itemCurrency === "USD" && itemExchangeRate > 0;
                  // p.total and p.amount are stored in the original currency (USD if USD, PHP if PHP)
                  // For USD items, use original values directly (not preview values which are scaled to PHP)
                  const totalOriginal = isUsdItem ? Number(p.total ?? 0) : Number(p.previewTotal ?? p.total ?? 0);
                  const unitCostOriginal = isUsdItem ? Number(p.amount ?? 0) : Number(p.previewUnitCost ?? p.amount ?? 0);
                  // Convert to PHP for display if USD
                  const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
                  const taxRate = normalizeParticularTaxRate(p);
                  const taxAmountPhp = taxRate > 0 ? computeParticularTaxAmount(totalPhp, taxRate) : 0;
                  const netAmountPhp = totalPhp - taxAmountPhp;
                  // Calculate USD equivalents for tax and net
                  const taxAmountUsd = isUsdItem && itemExchangeRate > 0 ? taxAmountPhp / itemExchangeRate : 0;
                  const netAmountUsd = isUsdItem && itemExchangeRate > 0 ? netAmountPhp / itemExchangeRate : 0;
                  const ewtAmountPhp = computeParticularEwtAmount(netAmountPhp, p.ewt_type, p.ewt_amount);
                  const ewtAmountUsd = isUsdItem && itemExchangeRate > 0 ? ewtAmountPhp / itemExchangeRate : 0;
                  const currencySymbol = isUsdItem ? "$" : "₱";
                  return (
                    <tr key={p.id || `${p.description}-${p.amount}-${p.quantity}`}>
                      <td className="border border-gray-800 px-3 py-2">
                        <div>{p.description}</div>
                        <div className="text-gray-600">
                          <strong>Qty:</strong> {p.quantity} &nbsp; <strong>Unit Cost:</strong> {currencySymbol}{formatMoney(unitCostOriginal, "en-PH")}
                        </div>
                        {isUsdItem && (
                          <div className="text-gray-600">
                            <strong>Currency:</strong> {itemCurrency} &nbsp; <strong>Conversion Rate:</strong> {itemExchangeRate.toFixed(4)}
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-800 px-3 py-2 text-right">
                        {taxRate > 0 ? (
                          isUsdItem ? (
                            <div className="flex flex-col">
                              <span>{currencySymbol}{formatMoney(netAmountUsd, "en-PH")}</span>
                              <span className="text-xs text-gray-500">₱{formatMoney(netAmountPhp, "en-PH")}</span>
                            </div>
                          ) : `₱${formatMoney(netAmountPhp, "en-PH")}`
                        ) : "—"}
                      </td>
                      <td className="border border-gray-800 px-3 py-2 text-right">
                        {taxRate > 0 ? (
                          isUsdItem ? (
                            <div className="flex flex-col">
                              <span>{currencySymbol}{formatMoney(taxAmountUsd, "en-PH")}</span>
                              <span className="text-xs text-gray-500">₱{formatMoney(taxAmountPhp, "en-PH")}</span>
                            </div>
                          ) : `₱${formatMoney(taxAmountPhp, "en-PH")}`
                        ) : "—"}
                      </td>
                      <td className="border border-gray-800 px-3 py-2 text-right">
                        {ewtAmountPhp > 0 ? (
                          isUsdItem ? (
                            <div className="flex flex-col">
                              <span>{currencySymbol}{formatMoney(ewtAmountUsd, "en-PH")}</span>
                              <span className="text-xs text-gray-500">₱{formatMoney(ewtAmountPhp, "en-PH")}</span>
                            </div>
                          ) : `₱${formatMoney(ewtAmountPhp, "en-PH")}`
                        ) : "—"}
                      </td>
                      <td className="border border-gray-800 px-3 py-2 text-right font-semibold">
                        {isUsdItem ? (
                          <div className="flex flex-col">
                            <span>{currencySymbol}{formatMoney(totalOriginal, "en-PH")}</span>
                            <span className="text-xs text-gray-500">₱{formatMoney(totalPhp, "en-PH")}</span>
                          </div>
                        ) : (
                          `₱${formatMoney(totalPhp, "en-PH")}`
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Empty row for spacing like print version */}
                <tr>
                  <td className="border border-gray-800 h-16"></td>
                  <td className="border border-gray-800"></td>
                  <td className="border border-gray-800"></td>
                  <td className="border border-gray-800"></td>
                  <td className="border border-gray-800"></td>
                </tr>
              </tbody>
            </table>

            {/* Total */}
            <div className="flex flex-col items-end gap-2 mb-4">
              <div className="border border-gray-800 px-4 py-2 min-w-[220px] flex items-center justify-between gap-3">
                <strong>TOTAL {getAmountCurrencyMeta(viewItem?.currency).code}</strong>
                <div className="shrink-0">
                  {String(viewItem?.id) === String(editItem?.id)
                    ? renderRequestTotal(editDraftTotalPhp, viewItem?.currency, viewItem?.exchange_rate)
                    : renderRequestTotal(getRequestTotalAmountPhp(viewItem), viewItem?.currency, viewItem?.exchange_rate)}
                </div>
              </div>
              {(() => {
                const isEditingThisItem = String(viewItem?.id) === String(editItem?.id);
                const previewRows = isEditingThisItem ? editParticularsPreview : currentViewParticularsPreview;
                const itemCurrency = String(viewItem?.currency || "PHP").toUpperCase();
                const itemExchangeRate = Number(viewItem?.exchange_rate || 1);
                const isUsdItem = itemCurrency === "USD" && itemExchangeRate > 0;
                const totalAmountPhp = isEditingThisItem ? editDraftTotalPhp : getRequestTotalAmountPhp(viewItem);
                const totalEwtAmountPhp = previewRows.reduce((sum, p) => {
                  const totalOriginal = isUsdItem ? Number(p.total ?? 0) : Number(p.previewTotal ?? p.total ?? 0);
                  const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
                  const taxRate = normalizeParticularTaxRate(p);
                  const taxAmountPhp = taxRate > 0 ? computeParticularTaxAmount(totalPhp, taxRate) : 0;
                  const netAmountPhp = totalPhp - taxAmountPhp;
                  return sum + computeParticularEwtAmount(netAmountPhp, p.ewt_type, p.ewt_amount);
                }, 0);
                if (totalEwtAmountPhp <= 0) return null;
                const netPayableAmountPhp = Number((totalAmountPhp - totalEwtAmountPhp).toFixed(2));
                return (
                  <>
                    <div className="border border-gray-800 px-4 py-2 min-w-[220px] flex items-center justify-between gap-3">
                      <strong>LESS: EWT</strong>
                      <div className="shrink-0">
                        (
                        {renderRequestTotal(totalEwtAmountPhp, viewItem?.currency, viewItem?.exchange_rate)}
                        )
                      </div>
                    </div>
                    <div className="border border-gray-800 px-4 py-2 min-w-[220px] flex items-center justify-between gap-3">
                      <strong>TOTAL AMOUNT DUE</strong>
                      <div className="shrink-0">
                        {renderRequestTotal(netPayableAmountPhp, viewItem?.currency, viewItem?.exchange_rate)}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Payment Info */}
            <div className="text-sm mb-6 space-y-1">
              <div><strong>Received from:</strong> _______________________</div>
              <div><strong>The amount of PESOS:</strong> __________________</div>
              <p className="text-xs text-gray-600 mt-1">In full payment of amount described above.</p>
              <div><strong>For Liquidation:</strong> {viewItem.for_liquidation ? "Yes" : "No"}</div>
              {viewItem.type === "non_operational" && (
                <div><strong>For Sales:</strong> {viewItem.for_sales ? "Yes" : "No"}</div>
              )}
              <div><strong>Rush request:</strong> {viewItem.rush ? "Yes" : "No"}</div>
              {viewItem.rush_reason && (
                <div><strong>Rush reason:</strong> {viewItem.rush_reason}</div>
              )}
              <div>
                <strong>Check voucher availability:</strong>{" "}
                {getRequestStatusKey(viewItem?.status) === "cancelled" &&
                (viewItem.has_check_voucher || viewItem.check_voucher_state === "voucher_created")
                  ? viewItem.check_voucher_no
                    ? `Voucher cancelled: ${viewItem.check_voucher_no}`
                    : "Voucher cancelled"
                  : viewItem.has_check_voucher || viewItem.check_voucher_state === "voucher_created"
                    ? viewItem.check_voucher_no
                      ? `Voucher created: ${viewItem.check_voucher_no}`
                      : "Voucher created"
                  : viewItem.can_create_check_voucher
                    ? "Available now"
                    : viewItem.check_voucher_available_from
                      ? `Available on ${formatDateKey(viewItem.check_voucher_available_from)}`
                      : "Waiting for approval"}
              </div>
              {viewItem.return_reason && (
                <div className="text-red-700">
                  <strong>Return reason:</strong> {viewItem.return_reason}
                </div>
              )}
            </div>

            {/* Signatures Grid */}
            <div className="grid grid-cols-3 gap-6 text-center text-sm">
              <div>
                <div className="h-16 flex items-end justify-center border-b border-gray-800 mb-1">
                  {viewItem.preparer_signature_url && (
                    <img src={viewItem.preparer_signature_url} alt="Preparer signature" className="max-h-14 object-contain" />
                  )}
                </div>
                <p><strong>Prepared by:</strong></p>
                <p>{viewItem.preparer_name || "—"}</p>
              </div>
              <div>
                <div className="h-16 flex items-end justify-center border-b border-gray-800 mb-1">
                  {viewItem.approver_signature_url ? (
                    <img src={viewItem.approver_signature_url} alt="Approver signature" className="max-h-14 object-contain" />
                  ) : null}
                </div>
                <p><strong>Approved by:</strong></p>
                <p>
                  {viewItem.approver_name || "____________________"}
                </p>
              </div>
              <div>
                <div className="h-16 flex items-end justify-center border-b border-gray-800 mb-1">
                  {viewItem.receiver_signature_url ? (
                    <img src={viewItem.receiver_signature_url} alt="Approver/Receiver signature" className="max-h-14 object-contain" />
                  ) : canApprove && Number(user?.user_id || user?.id) === 130 && user?.signature_url ? (
                    <span className="text-xs text-gray-400 italic">Your signature will appear here</span>
                  ) : null}
                </div>
                <p><strong>Approved/Received by:</strong></p>
                <p>
                  {viewItem.receiver_name ||
                    (canApprove && Number(user?.user_id || user?.id) === 130
                      ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "—"
                      : "____________________")}
                </p>
              </div>
            </div>

            {/* Status Badge */}
            {(() => {
              const user130AlreadyApproved =
                approverUserId === 130 && Number(viewItem.receiver_user_id || 0) === 130;
              const isCancelledRequest =
                normalizeRequestStatus(viewItem.status) === "cancelled";
              return (
                <>
            <div className="mt-6 flex items-center justify-center">
              <span
                className={`px-4 py-2 rounded-full text-sm font-semibold ${getRequestStatusBadgeClass(viewItem.status)}`}
              >
                Status: {formatRequestStatusLabel(viewItem.status)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end gap-3 flex-wrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <button
                onClick={() => setTrailItem(viewItem)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <i className="fa-solid fa-clock-rotate-left mr-2"></i>Trail
              </button>
              {![127, 130].includes(Number(user?.user_id || user?.id)) && (
                <button
                  onClick={() => openRequestEdit(viewItem)}
                  className="px-4 py-2 rounded-lg border border-blue/20 text-sm font-semibold text-blue hover:bg-blue/5"
                >
                  <i className="fa-solid fa-pen-to-square mr-2"></i>Edit
                </button>
              )}
              {Number(user?.user_id || user?.id) === viewItem.preparer_user_id &&
                viewItem.status === "returned" && (
                  <button
                    onClick={() => handleResubmit(viewItem)}
                    disabled={resubmitLoadingId === viewItem.id}
                    className="px-4 py-2 rounded-lg border border-purple-200 text-sm font-semibold text-purple-700 hover:bg-purple-50 disabled:opacity-60"
                  >
                    <i className="fa-solid fa-paper-plane mr-2"></i>
                    {resubmitLoadingId === viewItem.id ? "Resubmitting..." : "Resubmit"}
                  </button>
                )}
              {normalizeRequestStatus(viewItem.status) !== "cancelled" && (
                <button
                  onClick={() => {
                    setCancelError("");
                    setCancelItem(viewItem);
                  }}
                  className="px-4 py-2 rounded-lg border border-amber-200 text-sm font-semibold text-amber-700 hover:bg-amber-50"
                >
                  <i className="fa-solid fa-ban mr-2"></i>Cancel Request
                </button>
              )}
              <button
                onClick={() => setDeleteId(viewItem.id)}
                className="px-4 py-2 rounded-lg border border-red-200 text-sm font-semibold text-red hover:bg-red-50"
              >
                <i className="fa-solid fa-trash-can mr-2"></i>Delete
              </button>
              <button
                onClick={() => generatePrint(viewItem, viewShipment)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                <i className="fa-solid fa-print mr-2"></i>Print / Download
              </button>
              {canApprove && viewItem.status !== "approved" && (
                <>
                  {approverUserId === 127 && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 mr-3">
                      <label className="font-medium">Subject to liquidation?</label>
                      <select
                        value={liquidationChoice ? "yes" : "no"}
                        onChange={(e) => setLiquidationChoice(e.target.value === "yes")}
                        disabled={isCancelledRequest}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      setApproveLoading(true);
                      await authFetch(`${API_BASE_URL}/api/accounting/requests/${viewItem.id}/approve`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body:
                          approverUserId === 127
                            ? JSON.stringify({ for_liquidation: liquidationChoice })
                            : undefined,
                      });
                      setApproveLoading(false);
                      setViewItem(null);
                      load();
                    }}
                    disabled={
                      approveLoading ||
                      approvalCutoffBlocked ||
                      user130AlreadyApproved ||
                      isCancelledRequest
                    }
                    title={
                      isCancelledRequest
                        ? "Cancelled requests cannot be approved."
                        : user130AlreadyApproved
                        ? "User 130 has already approved this request."
                        : approvalCutoffBlocked
                        ? "This request was created after the 4:00 PM cutoff and cannot be approved today unless marked as rush."
                        : ""
                    }
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
                  >
                    <i className="fa-solid fa-check mr-2"></i>
                    {approveLoading
                      ? "Approving..."
                      : isCancelledRequest
                        ? "Approve"
                      : user130AlreadyApproved
                        ? "Approved"
                      : approvalCutoffBlocked
                        ? "Approve (Cutoff)"
                        : "Approve"}
                  </button>
                  <button
                    onClick={() => {
                      setReturnReason("");
                      setReturnError("");
                      setReturnOpen(true);
                    }}
                    disabled={approveLoading || returnLoading || isCancelledRequest}
                    title={isCancelledRequest ? "Cancelled requests cannot be returned." : ""}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
                  >
                    <i className="fa-solid fa-rotate-left mr-2"></i>
                    Return to Preparer
                  </button>
                  {isCancelledRequest && (
                    <div className="basis-full text-sm font-medium text-amber-700">
                      Note: This request is cancelled. Approve and Return to Preparer are disabled.
                    </div>
                  )}
                </>
              )}
              <button
                onClick={() => setViewItem(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
  </div>
);
};

export default RequestSummary;
